/**
 * FormField Component
 * Renders individual form control based on type.
 * Copied rendering pattern from atlas-form-player/src/designer/components/FormRenderer/FormField.tsx
 * Extended with chart/graph rendering (SVG-based, no external deps).
 */

import React, { memo, useCallback, useMemo, useState, Suspense, useRef, useEffect } from 'react';
import '../styles/controls.css';
import '../styles/form-field-view.css';
import '../styles/file-upload-view.css';
import ReactDOM from 'react-dom';
import type { FormControl, OperatingMode as FormOperatingMode } from '@atlas-forms/types-js';
import { EyeIcon, EyeOffIcon, BarChartIcon, CloseIcon, ChevronUpIcon, ChevronDownIcon } from '@atlas-forms/ui-components-react';
import { sanitizeInput, sanitizeEmail, stripHtml } from '../utils/sanitizeInput';
import { getModeConfig } from '../utils/modeConfig';
import { getGlobalReactRegistry } from '../controls/ReactControlRegistry';
import ImageControl from '../controls/displays/ImageControl';
import FileUploadViewMode from './FileUploadViewMode';
import QRCode from 'qrcode.react';
import JsBarcode from 'jsbarcode';
/**
 * Plan/05 §3B/§3C — the eager half of the opt-in "Edit using Expression Builder" trigger.
 *
 * `isExpressionBuilderEnabled` is the per-render gate for every text field in every form, so it must
 * be answerable without loading anything. `applyMaxLength` and the two output-format constants are
 * plain functions/literals. `ExpressionBuilderErrorBoundary` has to be eager by definition — it is
 * what renders when the lazy chunk below fails to arrive. None of them pulls in
 * `@bizfirst/expressions-designer`; the lazy `ExpressionBuilderTrigger` (declared with the other
 * `React.lazy` consts below) is the only thing that does.
 */
import {
  isExpressionBuilderEnabled,
  applyMaxLength,
  TEXT_EXPRESSION_DEFAULT_FORMAT,
  RICH_TEXT_EXPRESSION_DEFAULT_FORMAT,
} from '../expressionBuilder/expressionBuilderRuntime';
import { ExpressionBuilderErrorBoundary } from '../expressionBuilder/ExpressionBuilderErrorBoundary';
import {
  getInputStyle,
  getLabelStyle,
  getButtonStyle,
  getFieldWrapperStyle,
  getErrorStyle,
  getHelpStyle,
  type Theme,
} from '../utils/formStyles';
import { useFormTheme } from '../contexts/FormThemeContext';
import { validateFiles, FILE_VALIDATION_LIMITS } from '@atlas-forms/client-js';
import type { SelectOptionsSourceConfig } from '@atlas-forms/types-js';
import { fetchAuthToken } from '@passport/auth-session';
import { isInRole } from '../utils/roleAccess';
import type { CreateFormConfig } from './CreateRecordButton';

// Phase 2 - Performance: Code splitting for heavy controls
const HtmlControl = React.lazy(() => import('../controls/displays/HtmlControl'));
const VideoControl = React.lazy(() => import('../controls/displays/VideoControl').then(m => ({ default: m.VideoControl })));
const AudioControl = React.lazy(() => import('../controls/displays/AudioControl').then(m => ({ default: m.AudioControl })));
const IframeViewerControl = React.lazy(() => import('../controls/displays/IframeViewerControl').then(m => ({ default: m.IframeViewerControl })));
const PdfViewerControl = React.lazy(() => import('../controls/displays/PdfViewerControl').then(m => ({ default: m.PdfViewerControl })));
const ArticleControl = React.lazy(() => import('../controls/displays/ArticleControl').then(m => ({ default: m.ArticleControl })));
const MermaidControl = React.lazy(() => import('../controls/displays/MermaidControl').then(m => ({ default: m.MermaidControl })));
const FormContainerControl = React.lazy(() => import('../controls/layouts/FormContainerControl').then(m => ({ default: m.FormContainerControl })));
const GridControl = React.lazy(() => import('../controls/inputs/GridControl/GridControl').then(m => ({ default: m.GridControl })));
const LinkFormButton = React.lazy(() => import('./LinkFormButton'));
const CreateRecordButton = React.lazy(() => import('./CreateRecordButton'));
/**
 * Plan/05 §3B/§3C — the opt-in "Edit using Expression Builder" trigger for `text`/`textarea`/
 * `rich-text-editor`.
 *
 * Lazy for the same reason `FormContainerControl`/`GridControl` above are: the trigger pulls in
 * `@bizfirst/expressions-designer` and, through it, four `@tiptap/*` packages. Nobody should pay
 * that cost unless a field on their form actually sets `config.expressionBuilder.enabled: true`.
 * The eagerly-imported half lives with the other imports at the top of this file.
 */
const ExpressionBuilderTrigger = React.lazy(() => import('../expressionBuilder/ExpressionBuilderTrigger').then(m => ({ default: m.ExpressionBuilderTrigger })));

/**
 * The placeholder that holds the trigger's 38×38 slot open while its chunk is in flight, so the
 * field does not visibly reflow when the button arrives. `aria-hidden` because it conveys nothing —
 * `ExpressionBuilderTrigger`'s own `Suspense` renders a *labelled* loading button one level deeper,
 * for the second lazy boundary (the Expressions Designer itself).
 *
 * A module-scope constant rather than three identical inline copies: all three §3B/§3C case blocks
 * render the same element, and an element built once at module scope is also a stable reference
 * across every render of every field.
 */
const EXPRESSION_TRIGGER_SUSPENSE_FALLBACK = (
  <span aria-hidden="true" style={{ flex: '0 0 auto', width: '38px', height: '38px' }} />
);

/**
 * Fetches and normalizes options for a `select` control configured with
 * `config.optionsSource` (see `SelectOptionsSourceConfig`) — a "lookup" select whose option
 * list comes from a real API endpoint instead of a static `config.options` array. Handles
 * the handful of list-response envelope shapes already in use across this codebase's Go
 * controllers (`{ data: [...] }`, `{ Data: [...] }`, `{ items: [...] }`, or a bare array).
 * @private
 */
async function fetchLookupOptions(
  source: SelectOptionsSourceConfig,
): Promise<Array<{ value: string; label: string }>> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (source.auth !== 'none') {
    try {
      const session = fetchAuthToken();
      if (session?.token) headers.Authorization = `Bearer ${session.token}`;
    } catch (err) {
      console.warn('[FormField] fetchAuthToken() failed for lookup select; proceeding without an Authorization header:', err);
    }
  }

  const method = source.method ?? 'POST';
  const response = await fetch(source.endpoint, {
    method,
    headers,
    body: method === 'GET' ? undefined : JSON.stringify(source.requestBody ?? {}),
  });

  if (!response.ok) {
    throw new Error(`Lookup options request to '${source.endpoint}' failed: ${response.status} ${response.statusText}`);
  }

  const json = await response.json();
  const items: any[] = Array.isArray(json)
    ? json
    : (json?.data ?? json?.Data ?? json?.items ?? json?.Items ?? []);

  return (items as any[]).map((item) => ({
    value: String(item?.[source.valueField] ?? ''),
    label: String(item?.[source.labelField] ?? item?.[source.valueField] ?? ''),
  }));
}

/**
 * Extract files to pass to onChange based on mode
 * @private
 */
function getFilesForChangeEvent(
  files: File[],
  isMultipleMode: boolean
): File | File[] {
  if (isMultipleMode) {
    return files;
  }
  return files[0];
}

/**
 * Log file selection with mode awareness
 * @private
 */
function logFileSelection(
  files: File[],
  isMultipleMode: boolean,
  controlId: string,
  isDropEvent: boolean = false
): void {
  const mode = isMultipleMode ? 'MULTI-FILE MODE' : 'SINGLE-FILE MODE';
  const eventType = isDropEvent ? '(drag-drop)' : '(click)';

  if (isMultipleMode) {
    console.log(`[FormField] ✅ ${mode} ${eventType}: Passing all ${files.length} files to onChange`);
  } else {
    const ignoredCount = files.length - 1;
    console.log(`[FormField] ✅ ${mode} ${eventType}: Passing only first file to onChange`, {
      controlId,
      passedFile: files[0]?.name ?? 'unknown',
      ignoredFilesCount: ignoredCount,
    });
  }
}

export interface FormFieldProps {
  control: FormControl;
  value: any;
  error?: string;
  touched?: boolean;
  disabled?: boolean;
  mode?: FormOperatingMode;
  onChange?: (value: any) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  onButtonClick?: (buttonId: string, values: Record<string, any>) => void;
  formValues?: Record<string, any>;
}

// ─── Chart type guards ─────────────────────────────────────────────────────

const CHART_TYPES = new Set([
  'bar-chart', 'line-chart', 'pie-chart', 'area-chart', 'scatter-plot',
  'bubble-chart', 'histogram', 'waterfall-chart', 'tree-map',
  'circular-gauge', 'linear-gauge', 'thermometer', 'progress-ring',
  'gantt-chart', 'heatmap', 'sankey-diagram', 'network-graph',
  'analytics-dashboard', 'kpi-card',
]);

const DISPLAY_TYPES = new Set([
  'label', 'header', 'image', 'video', 'audio', 'article', 'html', 'css',
  'iframe-viewer', 'pdf-viewer', 'mermaid',
]);

const KNOWN_ADVANCED_TYPES = new Set([
  'qr-code-generator', 'barcode-generator', 'captcha',
  'rich-text-editor', 'code-editor', 'signature-pad', 'map', 'location-picker',
]);

// ─── Simple SVG charts ─────────────────────────────────────────────────────
// Phase 2 - Performance: Memoized with custom comparators to prevent unnecessary re-renders

/**
 * Custom comparator for chart props
 * Only re-render if data or theme actually changed
 */
const areChartsEqual = (
  prevProps: { data: any[]; theme: Theme },
  nextProps: { data: any[]; theme: Theme }
): boolean => {
  // Theme changed - must re-render
  if (prevProps.theme !== nextProps.theme) return false;

  // Data reference same - skip re-render
  if (prevProps.data === nextProps.data) return true;

  // Data length different - must re-render
  if (prevProps.data.length !== nextProps.data.length) return false;

  // Deep compare data values
  return prevProps.data.every((item, i) => {
    const prevVal = typeof item === 'number' ? item : item?.value;
    const nextVal = typeof nextProps.data[i] === 'number' ? nextProps.data[i] : nextProps.data[i]?.value;
    const prevName = typeof item === 'number' ? undefined : item?.name;
    const nextName = typeof nextProps.data[i] === 'number' ? undefined : nextProps.data[i]?.name;
    return prevVal === nextVal && prevName === nextName;
  });
};

const BarChartComponent = ({ data, theme }: { data: any[]; theme: Theme }) => {
  // Handle both number[] and object[] with {name, value}
  const values = data.map((item: any) => typeof item === 'number' ? item : (item?.value ?? 0));
  const names = data.map((item: any) => typeof item === 'number' ? `Item ${data.indexOf(item) + 1}` : (item?.name ?? ''));
  const max = Math.max(...values, 1);
  const w = 300; const h = 160; const barW = Math.floor(w / data.length) - 4;
  const chartId = `bar-chart-${Math.random().toString(36).slice(2, 9)}`;
  const tableId = `${chartId}-table`;

  // Accessibility: Summary for screen readers
  const summary = `Bar chart showing ${data.length} items. ${names.map((n, i) => `${n}: ${values[i]}`).join(', ')}`;

  return (
    <div>
      {/* Priority 1.3 - Accessibility: Chart with ARIA labels */}
      <svg
        width={w}
        height={h}
        role="img"
        aria-labelledby={`${chartId}-title`}
        aria-describedby={tableId}
        style={{ overflow: 'visible' }}
      >
        <title id={`${chartId}-title`}>Bar Chart</title>
        <desc>{summary}</desc>
        {values.map((v, i) => {
          const barH = Math.round((v / max) * (h - 40));
          const x = i * (barW + 4) + 2;
          const y = h - 30 - barH;
          return (
            <g key={i}>
              <title>{names[i]}: {v}</title>
              <rect x={x} y={y} width={barW} height={barH} rx={2}
                fill={theme === 'dark' ? '#22c55e' : '#16a34a'} opacity={0.85} />
              {/* Value label on bar */}
              {barH > 20 && (
                <text x={x + barW / 2} y={y + 12} textAnchor="middle" fontSize={9}
                  fill="white" fontWeight="bold">{v}</text>
              )}
              {/* Name label below bar */}
              <text x={x + barW / 2} y={h - 8} textAnchor="middle" fontSize={9}
                fill={theme === 'dark' ? '#94a3b8' : '#6b7280'}>{names[i]}</text>
            </g>
          );
        })}
      </svg>
      {/* Priority 1.3 - Accessibility: Data table fallback for screen readers */}
      <table id={tableId} style={{ position: 'absolute', left: '-10000px', width: '1px', height: '1px', overflow: 'hidden' }} aria-label="Bar chart data">
        <caption>Bar Chart Data</caption>
        <thead>
          <tr>
            <th scope="col">Item</th>
            <th scope="col">Value</th>
          </tr>
        </thead>
        <tbody>
          {names.map((name, i) => (
            <tr key={i}>
              <th scope="row">{name}</th>
              <td>{values[i]}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Phase 2 - Performance: Memoized BarChart component
const BarChart = React.memo(BarChartComponent, areChartsEqual);

const LineChartComponent = ({ data, theme }: { data: number[]; theme: Theme }) => {
  const max = Math.max(...data, 1); const min = Math.min(...data, 0);
  const w = 300; const h = 160; const pad = 15;
  const pts = data.map((v, i) => {
    const x = pad + (i / (data.length - 1 || 1)) * (w - pad * 2);
    const y = pad + ((max - v) / (max - min || 1)) * (h - pad * 2);
    return { x, y, v };
  });
  const areaBottom = `${w - pad},${h - pad} ${pad},${h - pad}`;
  const ptsString = pts.map(p => `${p.x},${p.y}`);
  const chartId = `line-chart-${Math.random().toString(36).slice(2, 9)}`;
  const tableId = `${chartId}-table`;

  // Accessibility: Summary for screen readers
  const summary = `Line chart showing ${data.length} data points. Values: ${data.join(', ')}. Range from ${min} to ${max}.`;

  return (
    <div>
      {/* Priority 1.3 - Accessibility: Chart with ARIA labels */}
      <svg
        width={w}
        height={h}
        role="img"
        aria-labelledby={`${chartId}-title`}
        aria-describedby={tableId}
      >
        <title id={`${chartId}-title`}>Line Chart</title>
        <desc>{summary}</desc>
        <polyline points={[...ptsString, ...areaBottom.split(' ')].join(' ')}
          fill={theme === 'dark' ? 'rgba(59,130,246,0.15)' : 'rgba(37,99,235,0.1)'} stroke="none" />
        <polyline points={ptsString.join(' ')} fill="none"
          stroke={theme === 'dark' ? '#22c55e' : '#16a34a'} strokeWidth={2} strokeLinejoin="round" />
        {pts.map((pt, i) => (
          <g key={i}>
            <title>Point {i + 1}: {pt.v}</title>
            <circle cx={pt.x} cy={pt.y} r={3}
              fill={theme === 'dark' ? '#22c55e' : '#16a34a'} />
            {/* Value label above point */}
            <text x={pt.x} y={pt.y - 8} textAnchor="middle" fontSize={8}
              fill={theme === 'dark' ? '#e5e7eb' : '#374151'} fontWeight="bold">{pt.v}</text>
          </g>
        ))}
      </svg>
      {/* Priority 1.3 - Accessibility: Data table fallback for screen readers */}
      <table id={tableId} style={{ position: 'absolute', left: '-10000px', width: '1px', height: '1px', overflow: 'hidden' }} aria-label="Line chart data">
        <caption>Line Chart Data</caption>
        <thead>
          <tr>
            <th scope="col">Point</th>
            <th scope="col">Value</th>
          </tr>
        </thead>
        <tbody>
          {data.map((value, i) => (
            <tr key={i}>
              <th scope="row">Point {i + 1}</th>
              <td>{value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Phase 2 - Performance: Custom comparator for LineChart (number[] data)
const areLineChartsEqual = (
  prevProps: { data: number[]; theme: Theme },
  nextProps: { data: number[]; theme: Theme }
): boolean => {
  if (prevProps.theme !== nextProps.theme) return false;
  if (prevProps.data === nextProps.data) return true;
  if (prevProps.data.length !== nextProps.data.length) return false;
  return prevProps.data.every((val, i) => val === nextProps.data[i]);
};

const LineChart = React.memo(LineChartComponent, areLineChartsEqual);

const AreaChartComponent = ({ data, theme }: { data: number[]; theme: Theme }) => {
  const max = Math.max(...data, 1); const min = Math.min(...data, 0);
  const w = 300; const h = 160; const pad = 15;
  const pts = data.map((v, i) => {
    const x = pad + (i / (data.length - 1 || 1)) * (w - pad * 2);
    const y = pad + ((max - v) / (max - min || 1)) * (h - pad * 2);
    return { x, y, v };
  });
  const linePoints = pts.map(p => `${p.x},${p.y}`).join(' ');
  const areaPoints = [
    ...pts.map(p => `${p.x},${p.y}`),
    `${pts[pts.length - 1].x},${h - pad}`,
    `${pts[0].x},${h - pad}`,
  ].join(' ');
  const fillColor = theme === 'dark' ? '#22c55e' : '#16a34a';
  return (
    <svg width={w} height={h}>
      <polygon points={areaPoints} fill={fillColor} opacity={0.2} />
      <polyline points={linePoints} fill="none"
        stroke={fillColor} strokeWidth={2} strokeLinejoin="round" />
      {pts.map((p, i) => (
        <g key={i}>
          <title>Point {i + 1}: {p.v}</title>
          <circle cx={p.x} cy={p.y} r={3} fill={fillColor} />
          {/* Value label above point */}
          <text x={p.x} y={p.y - 8} textAnchor="middle" fontSize={8}
            fill={theme === 'dark' ? '#e5e7eb' : '#374151'} fontWeight="bold">{p.v}</text>
        </g>
      ))}
    </svg>
  );
};

const AreaChart = React.memo(AreaChartComponent, areLineChartsEqual);

const HistogramChartComponent = ({ data, theme }: { data: number[]; theme: Theme }) => {
  const max = Math.max(...data, 1);
  const w = 300; const h = 160; const barW = Math.floor(w / data.length);
  const colors = theme === 'dark'
    ? ['#22c55e', '#10b981', '#059669']
    : ['#16a34a', '#15803d', '#166534'];
  return (
    <svg width={w} height={h} style={{ overflow: 'visible' }}>
      {data.map((v, i) => {
        const barH = Math.round((v / max) * (h - 30));
        const x = i * barW;
        const y = h - 25 - barH;
        return (
          <g key={i}>
            <title>Bin {i + 1}: {v}</title>
            <rect x={x} y={y} width={barW - 1} height={barH}
              fill={colors[i % colors.length]} opacity={0.85} />
            {/* Value label on bar */}
            {barH > 15 && (
              <text x={x + barW / 2} y={y + 12} textAnchor="middle" fontSize={9}
                fill="white" fontWeight="bold">{v}</text>
            )}
            {/* Index label below bar */}
            <text x={x + barW / 2} y={h - 5} textAnchor="middle"
              fontSize={9} fill={theme === 'dark' ? '#94a3b8' : '#6b7280'}>{i + 1}</text>
          </g>
        );
      })}
    </svg>
  );
};

const HistogramChart = React.memo(HistogramChartComponent, areLineChartsEqual);

const WaterfallChartComponent = ({ data, theme }: { data: number[]; theme: Theme }) => {
  const w = 300; const h = 160; const barW = Math.floor(w / data.length) - 4;
  let cumulative = 0;
  const segments = data.map((v) => {
    const start = cumulative;
    cumulative += v;
    return { start, end: cumulative, value: v };
  });
  const maxVal = Math.max(...segments.map(s => Math.max(Math.abs(s.start), Math.abs(s.end))), 1);
  const scale = (h - 24) / maxVal;
  const baseline = h - 20;
  const posColor = theme === 'dark' ? '#22c55e' : '#16a34a';
  const negColor = theme === 'dark' ? '#ef4444' : '#dc2626';
  return (
    <svg width={w} height={h} style={{ overflow: 'visible' }}>
      {segments.map((seg, i) => {
        const x = i * (barW + 4) + 2;
        const top = baseline - Math.max(seg.start, seg.end) * scale;
        const bottom = baseline - Math.min(seg.start, seg.end) * scale;
        const barH = bottom - top;
        return (
          <g key={i}>
            <title>Step {i + 1}: {seg.value} (Total: {seg.end})</title>
            <rect x={x} y={top} width={barW} height={Math.max(barH, 1)} rx={2}
              fill={seg.value >= 0 ? posColor : negColor} opacity={0.85} />
            {i < segments.length - 1 && (
              <line x1={x + barW} y1={baseline - seg.end * scale}
                x2={x + barW + 4} y2={baseline - seg.end * scale}
                stroke={theme === 'dark' ? '#475569' : '#9ca3af'} strokeWidth={1} strokeDasharray="2 2" />
            )}
            <text x={x + barW / 2} y={h - 4} textAnchor="middle"
              fontSize={10} fill={theme === 'dark' ? '#94a3b8' : '#6b7280'}>{seg.value}</text>
          </g>
        );
      })}
    </svg>
  );
};

const WaterfallChart = React.memo(WaterfallChartComponent, areLineChartsEqual);

const TreeMapChartComponent = ({ data, theme }: { data: number[]; theme: Theme }) => {
  const total = data.reduce((s, v) => s + v, 0) || 1;
  const w = 300; const h = 160;
  const colors = theme === 'dark'
    ? ['#22c55e', '#10b981', '#059669', '#047857', '#065f46', '#064e3b', '#14532d']
    : ['#16a34a', '#15803d', '#166534', '#14532d', '#4ade80', '#86efac', '#bbf7d0'];
  // Simple squarified treemap: lay out rectangles left-to-right, row by row
  const rects: Array<{ x: number; y: number; w: number; h: number; v: number }> = [];
  let cx = 0; let cy = 0; let rowH = h;
  const sorted = [...data].sort((a, b) => b - a);
  let remaining = total;
  let rowItems: number[] = [];
  let rowWidth = w;

  for (const v of sorted) {
    rowItems.push(v);
    const rowTotal = rowItems.reduce((s, x) => s + x, 0);
    // If adding this item makes the aspect ratio worse, flush the row
    if (rowItems.length > 1) {
      const itemH = (rowTotal / remaining) * rowH;
      const lastW = (v / rowTotal) * rowWidth;
      if (itemH > 0 && (lastW / itemH > 4 || itemH / lastW > 4) && rowItems.length > 2) {
        // Flush without last item
        rowItems.pop();
        const flushTotal = rowItems.reduce((s, x) => s + x, 0);
        const flushH = (flushTotal / remaining) * rowH;
        let ix = cx;
        for (const rv of rowItems) {
          const rw = (rv / flushTotal) * rowWidth;
          rects.push({ x: ix, y: cy, w: rw, h: flushH, v: rv });
          ix += rw;
        }
        remaining -= flushTotal;
        cy += flushH;
        rowH -= flushH;
        rowItems = [v];
      }
    }
  }
  // Flush remaining
  if (rowItems.length > 0) {
    const rowTotal = rowItems.reduce((s, x) => s + x, 0);
    let ix = cx;
    for (const rv of rowItems) {
      const rw = (rv / rowTotal) * rowWidth;
      rects.push({ x: ix, y: cy, w: rw, h: rowH, v: rv });
      ix += rw;
    }
  }

  return (
    <svg width={w} height={h} style={{ overflow: 'visible' }}>
      {rects.map((r, i) => {
        const percentage = Math.round((r.v / total) * 100);
        return (
          <g key={i}>
            <title>Item {i + 1}: {r.v} ({percentage}%)</title>
            <rect x={r.x} y={r.y} width={Math.max(r.w - 1, 0)} height={Math.max(r.h - 1, 0)}
              fill={colors[i % colors.length]} rx={2} opacity={0.85} />
            {r.w > 28 && r.h > 16 && (
              <text x={r.x + r.w / 2} y={r.y + r.h / 2 + 4} textAnchor="middle"
                fontSize={11} fontWeight={600} fill="#fff">{r.v}</text>
            )}
          </g>
        );
      })}
    </svg>
  );
};

const TreeMapChart = React.memo(TreeMapChartComponent, areLineChartsEqual);

const PieChartComponent = ({ data, theme }: { data: any[]; theme: Theme }) => {
  const colors = ['#22c55e', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
  const values = data.map((item: any) => typeof item === 'number' ? item : (item?.value ?? 0));
  const names = data.map((item: any) => typeof item === 'number' ? `Item ${data.indexOf(item) + 1}` : (item?.name ?? ''));
  const total = values.reduce((s, v) => s + v, 0) || 1;
  const cx = 100; const cy = 75; const r = 65;
  const chartId = `pie-chart-${Math.random().toString(36).slice(2, 9)}`;
  const tableId = `${chartId}-table`;

  // Accessibility: Summary for screen readers
  const summary = `Pie chart showing ${data.length} categories. ${names.map((n, i) => {
    const pct = Math.round((values[i] / total) * 100);
    return `${n}: ${values[i]} (${pct}%)`;
  }).join(', ')}`;

  let angle = -Math.PI / 2;
  return (
    <div>
      {/* Priority 1.3 - Accessibility: Chart with ARIA labels */}
      <svg
        width={280}
        height={150}
        role="img"
        aria-labelledby={`${chartId}-title`}
        aria-describedby={tableId}
        style={{ overflow: 'visible' }}
      >
        <title id={`${chartId}-title`}>Pie Chart</title>
        <desc>{summary}</desc>
        {values.map((v, i) => {
          const sweep = (v / total) * 2 * Math.PI;
          const x1 = cx + r * Math.cos(angle); const y1 = cy + r * Math.sin(angle);
          const midAngle = angle + sweep / 2;
          angle += sweep;
          const x2 = cx + r * Math.cos(angle); const y2 = cy + r * Math.sin(angle);
          const large = sweep > Math.PI ? 1 : 0;

          // Label position in the middle of the slice
          const labelRadius = r * 0.65;
          const labelX = cx + labelRadius * Math.cos(midAngle);
          const labelY = cy + labelRadius * Math.sin(midAngle);
          const percentage = Math.round((v / total) * 100);

          return (
            <g key={i}>
              <title>{names[i]}: {v} ({percentage}%)</title>
              <path
                d={`M${cx},${cy} L${x1},${y1} A${r},${r} 0 ${large},1 ${x2},${y2} Z`}
                fill={colors[i % colors.length]} opacity={0.9} stroke="#fff" strokeWidth={1} />
              {/* Value and percentage label */}
              <text x={labelX} y={labelY - 5} textAnchor="middle" fontSize={9}
                fill="white" fontWeight="bold">{v}</text>
              <text x={labelX} y={labelY + 8} textAnchor="middle" fontSize={8}
                fill="white">{percentage}%</text>
            </g>
          );
        })}
        {/* Legend */}
        {values.map((v, i) => (
          <g key={`legend-${i}`}>
            <rect x={5} y={100 + i * 12} width={8} height={8} fill={colors[i % colors.length]} opacity={0.9} />
            <text x={16} y={107 + i * 12} fontSize={8} fill={theme === 'dark' ? '#e5e7eb' : '#374151'}>
              {names[i]?.substring(0, 8)}
            </text>
          </g>
        ))}
      </svg>
      {/* Priority 1.3 - Accessibility: Data table fallback for screen readers */}
      <table id={tableId} style={{ position: 'absolute', left: '-10000px', width: '1px', height: '1px', overflow: 'hidden' }} aria-label="Pie chart data">
        <caption>Pie Chart Data</caption>
        <thead>
          <tr>
            <th scope="col">Category</th>
            <th scope="col">Value</th>
            <th scope="col">Percentage</th>
          </tr>
        </thead>
        <tbody>
          {names.map((name, i) => {
            const percentage = Math.round((values[i] / total) * 100);
            return (
              <tr key={i}>
                <th scope="row">{name}</th>
                <td>{values[i]}</td>
                <td>{percentage}%</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const PieChart = React.memo(PieChartComponent, areChartsEqual);

const HeatmapChartComponent = ({ data, theme }: { data: number[]; theme: Theme }) => {
  // Arrange data into a grid
  const cols = Math.ceil(Math.sqrt(data.length));
  const rows = Math.ceil(data.length / cols);
  const max = Math.max(...data, 1);
  const cellW = Math.floor(300 / cols);
  const cellH = Math.floor(160 / rows);
  const baseColor = theme === 'dark' ? [34, 197, 94] : [22, 163, 74]; // green
  return (
    <svg width={cols * cellW} height={rows * cellH} style={{ overflow: 'visible' }}>
      {data.map((v, i) => {
        const col = i % cols;
        const row = Math.floor(i / cols);
        const intensity = v / max;
        const fill = `rgba(${baseColor[0]},${baseColor[1]},${baseColor[2]},${0.15 + intensity * 0.75})`;
        return (
          <g key={i}>
            <rect x={col * cellW} y={row * cellH} width={cellW - 1} height={cellH - 1}
              fill={fill} rx={2} />
            <text x={col * cellW + cellW / 2} y={row * cellH + cellH / 2 + 4}
              textAnchor="middle" fontSize={11} fontWeight={600}
              fill={intensity > 0.5 ? '#fff' : (theme === 'dark' ? '#94a3b8' : '#374151')}>
              {v}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

const HeatmapChart = React.memo(HeatmapChartComponent, areLineChartsEqual);

const SankeyChartComponent = ({ data, theme }: { data: number[]; theme: Theme }) => {
  const w = 300; const h = 180;
  const colors = ['#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6'];
  // Build simple sankey: left nodes → right nodes with flow widths
  const total = data.reduce((s, v) => s + v, 0) || 1;
  const leftLabels = data.map((_, i) => `S${i + 1}`);
  const rightLabels = data.map((_, i) => `T${i + 1}`);
  const nodeH = h / data.length;
  return (
    <svg width={w} height={h} style={{ overflow: 'visible' }}>
      {data.map((v, i) => {
        const flowH = Math.max(4, (v / total) * (h - 10));
        const ly = i * nodeH + (nodeH - flowH) / 2;
        // Route to a shuffled right-side position for visual interest
        const ri = (i + 1) % data.length;
        const ry = ri * nodeH + (nodeH - flowH) / 2;
        const color = colors[i % colors.length];
        return (
          <g key={i}>
            {/* Left node */}
            <rect x={0} y={ly} width={16} height={flowH} fill={color} rx={2} />
            {/* Flow path */}
            <path d={`M16,${ly + flowH / 2} C${w / 2},${ly + flowH / 2} ${w / 2},${ry + flowH / 2} ${w - 16},${ry + flowH / 2}`}
              stroke={color} strokeWidth={flowH} fill="none" opacity={0.25} />
            {/* Right node */}
            <rect x={w - 16} y={ry} width={16} height={flowH} fill={color} rx={2} />
            {/* Labels */}
            <text x={20} y={ly + flowH / 2 + 4} fontSize={10} fill={theme === 'dark' ? '#94a3b8' : '#6b7280'}>{leftLabels[i]}</text>
            <text x={w - 20} y={ry + flowH / 2 + 4} textAnchor="end" fontSize={10} fill={theme === 'dark' ? '#94a3b8' : '#6b7280'}>{rightLabels[ri]}</text>
          </g>
        );
      })}
    </svg>
  );
};

const SankeyChart = React.memo(SankeyChartComponent, areLineChartsEqual);

const NetworkGraphChartComponent = ({ data, theme }: { data: number[]; theme: Theme }) => {
  const w = 300; const h = 200;
  const nodeCount = Math.min(data.length, 10);
  const cx = w / 2; const cy = h / 2; const r = 70;
  const nodeColor = theme === 'dark' ? '#22c55e' : '#16a34a';
  const edgeColor = theme === 'dark' ? 'rgba(34,197,94,0.3)' : 'rgba(22,163,74,0.2)';
  const textColor = theme === 'dark' ? '#e5e7eb' : '#111827';
  // Place nodes in a circle
  const nodes = data.slice(0, nodeCount).map((v, i) => {
    const angle = (2 * Math.PI * i) / nodeCount - Math.PI / 2;
    return { x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle), v, i };
  });
  // Draw edges between consecutive nodes and some cross-connections
  const edges: Array<{ from: number; to: number }> = [];
  for (let i = 0; i < nodes.length; i++) {
    edges.push({ from: i, to: (i + 1) % nodes.length });
    if (i + 2 < nodes.length) edges.push({ from: i, to: i + 2 });
  }
  return (
    <svg width={w} height={h}>
      {edges.map((e, i) => (
        <line key={`e${i}`} x1={nodes[e.from].x} y1={nodes[e.from].y}
          x2={nodes[e.to].x} y2={nodes[e.to].y}
          stroke={edgeColor} strokeWidth={1.5} />
      ))}
      {nodes.map((n) => (
        <g key={`n${n.i}`}>
          <circle cx={n.x} cy={n.y} r={Math.max(10, n.v / 8)} fill={nodeColor} opacity={0.85} />
          <text x={n.x} y={n.y + 4} textAnchor="middle" fontSize={10} fontWeight={700} fill={textColor}>
            {n.v}
          </text>
        </g>
      ))}
    </svg>
  );
};

const NetworkGraphChart = React.memo(NetworkGraphChartComponent, areLineChartsEqual);

const GanttChartComponent = ({ data, theme }: { data: number[]; theme: Theme }) => {
  const w = 300; const h = Math.max(120, data.length * 28 + 20);
  const maxVal = Math.max(...data, 1);
  const colors = ['#22c55e', '#3b82f6', '#f59e0b', '#8b5cf6', '#ec4899', '#14b8a6', '#ef4444'];
  const barH = 18;
  const labelW = 40;
  let cumStart = 0;
  return (
    <svg width={w} height={h} style={{ overflow: 'visible' }}>
      {/* Grid lines */}
      {[0, 0.25, 0.5, 0.75, 1].map((pct) => {
        const x = labelW + pct * (w - labelW);
        return <line key={pct} x1={x} y1={0} x2={x} y2={h} stroke={theme === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f5f9'} />;
      })}
      {data.map((v, i) => {
        const y = i * 28 + 4;
        const totalSpan = data.reduce((s, x) => s + x, 0);
        const barStart = labelW + (cumStart / totalSpan) * (w - labelW);
        const barWidth = Math.max(4, (v / totalSpan) * (w - labelW));
        cumStart += v;
        const color = colors[i % colors.length];
        return (
          <g key={i}>
            <text x={2} y={y + barH / 2 + 4} fontSize={10} fill={theme === 'dark' ? '#94a3b8' : '#6b7280'}>
              T{i + 1}
            </text>
            <rect x={barStart} y={y} width={barWidth} height={barH} fill={color} rx={3} opacity={0.85} />
            <text x={barStart + barWidth / 2} y={y + barH / 2 + 4} textAnchor="middle"
              fontSize={10} fontWeight={600} fill="#fff">{v}</text>
          </g>
        );
      })}
    </svg>
  );
};

const GanttChart = React.memo(GanttChartComponent, areLineChartsEqual);

// ─── Extracted stateful sub-components (avoid Rules of Hooks violations) ────

/** Props shared by layout sub-components rendered inside FormField's switch */
interface LayoutControlProps {
  control: FormControl;
  theme: Theme;
  mode: FormOperatingMode;
  inputStyle: React.CSSProperties;
  isDisabled: boolean;
  value: any;
  onChange?: (v: any) => void;
  onBlur?: () => void;
}

// Phase 2 - Performance: Memoized expensive controls
const TagInputControlComponent = ({ control, theme, inputStyle, isDisabled, value, onChange, onBlur }: LayoutControlProps) => {
  const tags = Array.isArray(value) ? (value as string[]) : (typeof value === 'string' && value ? value.split(',') : []);
  const [tagInput, setTagInput] = React.useState('');
  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: tags.length ? '8px' : 0 }}>
        {tags.map((t, i) => (
          <span key={`${t}-${i}`} style={{
            display: 'inline-flex', alignItems: 'center', gap: '4px',
            padding: '3px 10px', borderRadius: '12px', fontSize: '13px',
            background: theme === 'dark' ? 'rgba(34,197,94,0.15)' : '#dcfce7',
            color: theme === 'dark' ? '#4ade80' : '#15803d',
          }}>
            {t}
            {!isDisabled && <button type="button" onClick={() => onChange?.(tags.filter((_, j) => j !== i))}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: '14px', lineHeight: 1, color: 'inherit' }}><CloseIcon width={12} height={12} /></button>}
          </span>
        ))}
      </div>
      {!isDisabled && (
        <input type="text" style={inputStyle} value={tagInput} placeholder={control.placeholder ?? 'Type and press Enter'}
          onChange={(e) => setTagInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && tagInput.trim()) { e.preventDefault(); onChange?.([...tags, tagInput.trim()]); setTagInput(''); }
            if (e.key === 'Backspace' && !tagInput && tags.length) { onChange?.(tags.slice(0, -1)); }
          }}
          onBlur={onBlur} />
      )}
    </div>
  );
};

const TagInputControl = React.memo(TagInputControlComponent);

const CollapsiblePanelControlComponent = ({ control, theme, mode }: LayoutControlProps) => {
  const [panelOpen, setPanelOpen] = React.useState(control.config?.defaultOpen !== false);
  const panelChildren = (control.config?.controls as FormControl[] | undefined) ?? [];
  return (
    <div style={{ border: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}`, borderRadius: '8px', overflow: 'hidden' }}>
      <button type="button" onClick={() => setPanelOpen(!panelOpen)}
        aria-expanded={panelOpen}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 14px', border: 'none', cursor: 'pointer',
          background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : '#f9fafb',
          color: theme === 'dark' ? '#e2e8f0' : '#111827', fontSize: '14px', fontWeight: 600,
        }}>
        <span>{control.label || 'Panel'}</span>
        <span style={{ fontSize: '12px', transition: 'transform 0.2s', transform: panelOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>▾</span>
      </button>
      {panelOpen && (
        <div style={{ padding: '14px', borderTop: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f5f9'}` }}>
          {panelChildren.length > 0
            ? panelChildren.map((child) => <FormField key={child.id} control={child} value={undefined} mode={mode} onChange={() => {}} />)
            : <div style={{ fontSize: '13px', color: theme === 'dark' ? '#475569' : '#9ca3af' }}>Panel content area</div>}
        </div>
      )}
    </div>
  );
};

const CollapsiblePanelControl = React.memo(CollapsiblePanelControlComponent);

const AccordionControlComponent = ({ control, theme, mode }: LayoutControlProps) => {
  const accSections = (control.config?.sections as Array<{ label: string; controls?: FormControl[] }> | undefined)
    ?? [{ label: 'Section 1' }, { label: 'Section 2' }, { label: 'Section 3' }];
  const [accOpen, setAccOpen] = React.useState<number>(0);
  return (
    <div style={{ border: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}`, borderRadius: '8px', overflow: 'hidden' }}>
      {accSections.map((sec, i) => (
        <div key={i}>
          <button type="button" onClick={() => setAccOpen(accOpen === i ? -1 : i)}
            aria-expanded={accOpen === i}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '10px 14px', border: 'none', cursor: 'pointer',
              borderTop: i > 0 ? `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f5f9'}` : 'none',
              background: accOpen === i
                ? (theme === 'dark' ? 'rgba(34,197,94,0.08)' : 'rgba(22,163,74,0.04)')
                : (theme === 'dark' ? 'rgba(255,255,255,0.03)' : '#fff'),
              color: accOpen === i ? (theme === 'dark' ? '#22c55e' : '#16a34a') : (theme === 'dark' ? '#e2e8f0' : '#111827'),
              fontSize: '13px', fontWeight: 600,
            }}>
            <span>{sec.label}</span>
            <span style={{ fontSize: '11px', transition: 'transform 0.2s', transform: accOpen === i ? 'rotate(180deg)' : 'rotate(0deg)' }}>▾</span>
          </button>
          {accOpen === i && (
            <div style={{ padding: '14px', borderTop: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f5f9'}` }}>
              {(sec.controls && sec.controls.length > 0)
                ? sec.controls.map((child) => <FormField key={child.id} control={child} value={undefined} mode={mode} onChange={() => {}} />)
                : <div style={{ fontSize: '13px', color: theme === 'dark' ? '#475569' : '#9ca3af' }}>Section content</div>}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const AccordionControl = React.memo(AccordionControlComponent);

const EnhancedTabsControlComponent = ({ control, theme, mode }: LayoutControlProps) => {
  const tabItems = (control.config?.tabs as Array<{ id?: string; label: string; controls?: FormControl[] }> | undefined)
    ?? [{ label: 'Tab 1' }, { label: 'Tab 2' }, { label: 'Tab 3' }];
  const [activeTabIdx, setActiveTabIdx] = React.useState(0);
  const activeTabContent = tabItems[activeTabIdx];
  return (
    <div style={{ border: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}`, borderRadius: '8px', overflow: 'hidden' }}>
      <div role="tablist" style={{ display: 'flex', borderBottom: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f5f9'}`,
        background: theme === 'dark' ? 'rgba(255,255,255,0.03)' : '#f9fafb' }}>
        {tabItems.map((t, i) => (
          <button key={i} type="button" role="tab" aria-selected={activeTabIdx === i} onClick={() => setActiveTabIdx(i)}
            style={{
              padding: '9px 16px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 600,
              borderBottom: `2px solid ${activeTabIdx === i ? (theme === 'dark' ? '#22c55e' : '#16a34a') : 'transparent'}`,
              background: 'transparent',
              color: activeTabIdx === i ? (theme === 'dark' ? '#22c55e' : '#16a34a') : (theme === 'dark' ? '#94a3b8' : '#6b7280'),
            }}>
            {t.label}
          </button>
        ))}
      </div>
      <div role="tabpanel" style={{ padding: '14px' }}>
        {(activeTabContent?.controls && activeTabContent.controls.length > 0)
          ? activeTabContent.controls.map((child) => <FormField key={child.id} control={child} value={undefined} mode={mode} onChange={() => {}} />)
          : <div style={{ fontSize: '13px', color: theme === 'dark' ? '#475569' : '#9ca3af' }}>{activeTabContent?.label ?? 'Tab'} content</div>}
      </div>
    </div>
  );
};

const EnhancedTabsControl = React.memo(EnhancedTabsControlComponent);

const ModalControlComponent = ({ control, theme, mode }: LayoutControlProps) => {
  const [modalOpen, setModalOpen] = React.useState(false);
  const modalChildren = (control.config?.controls as FormControl[] | undefined) ?? [];

  // Escape key + scroll lock
  React.useEffect(() => {
    if (!modalOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setModalOpen(false); };
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = prev;
    };
  }, [modalOpen]);

  const overlay = modalOpen ? (
    <div role="dialog" aria-modal="true" aria-label={control.label || 'Dialog'} style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={(e) => { if (e.target === e.currentTarget) setModalOpen(false); }}>
      <div style={{
        width: '90%', maxWidth: '500px', maxHeight: '80vh', overflow: 'auto',
        borderRadius: '12px', padding: '20px',
        background: theme === 'dark' ? '#1e293b' : '#fff',
        border: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}`,
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: theme === 'dark' ? '#f1f5f9' : '#111827' }}>
            {control.label || 'Dialog'}
          </h3>
          <button type="button" onClick={() => setModalOpen(false)}
            aria-label="Close dialog"
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: theme === 'dark' ? '#94a3b8' : '#6b7280' }}><CloseIcon width={16} height={16} /></button>
        </div>
        {modalChildren.length > 0
          ? modalChildren.map((child) => <FormField key={child.id} control={child} value={undefined} mode={mode} onChange={() => {}} />)
          : <div style={{ fontSize: '13px', color: theme === 'dark' ? '#475569' : '#9ca3af', padding: '20px 0', textAlign: 'center' }}>Dialog content area</div>}
      </div>
    </div>
  ) : null;

  return (
    <>
      <button type="button" onClick={() => setModalOpen(true)}
        style={{
          padding: '8px 16px', borderRadius: '6px', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
          border: `1px solid ${theme === 'dark' ? 'rgba(34,197,94,0.4)' : '#86efac'}`,
          background: theme === 'dark' ? 'rgba(34,197,94,0.1)' : '#f0fdf4',
          color: theme === 'dark' ? '#22c55e' : '#16a34a',
        }}>
        {control.label || 'Open Dialog'}
      </button>
      {typeof document !== 'undefined' && overlay
        ? ReactDOM.createPortal(overlay, document.body)
        : overlay}
    </>
  );
};

const ModalControl = React.memo(ModalControlComponent);

const StepperControlComponent = ({ control, theme, mode }: LayoutControlProps) => {
  const steps = (control.config?.steps as Array<{ label: string; controls?: FormControl[] }> | undefined)
    ?? [{ label: 'Step 1' }, { label: 'Step 2' }, { label: 'Step 3' }];
  const [stepIdx, setStepIdx] = React.useState(0);
  const currentStep = steps[stepIdx];
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '16px' }}>
        {steps.map((s, i) => (
          <React.Fragment key={i}>
            {i > 0 && <div style={{ flex: 1, height: '2px', background: i <= stepIdx ? (theme === 'dark' ? '#22c55e' : '#16a34a') : (theme === 'dark' ? 'rgba(255,255,255,0.1)' : '#e5e7eb') }} />}
            <button type="button" onClick={() => setStepIdx(i)}
              aria-current={i === stepIdx ? 'step' : undefined}
              style={{
                width: '28px', height: '28px', borderRadius: '50%', border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700, flexShrink: 0,
                background: i <= stepIdx ? (theme === 'dark' ? '#22c55e' : '#16a34a') : (theme === 'dark' ? 'rgba(255,255,255,0.1)' : '#e5e7eb'),
                color: i <= stepIdx ? '#fff' : (theme === 'dark' ? '#64748b' : '#9ca3af'),
              }}>
              {i + 1}
            </button>
          </React.Fragment>
        ))}
      </div>
      <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', color: theme === 'dark' ? '#e2e8f0' : '#111827' }}>
        {currentStep?.label}
      </div>
      <div style={{ minHeight: '60px' }}>
        {(currentStep?.controls && currentStep.controls.length > 0)
          ? currentStep.controls.map((child) => <FormField key={child.id} control={child} value={undefined} mode={mode} onChange={() => {}} />)
          : <div style={{ fontSize: '13px', color: theme === 'dark' ? '#475569' : '#9ca3af' }}>{currentStep?.label ?? 'Step'} content</div>}
      </div>
      <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
        <button type="button" disabled={stepIdx === 0} onClick={() => setStepIdx(stepIdx - 1)}
          style={{ padding: '6px 14px', borderRadius: '6px', fontSize: '13px', fontWeight: 600, cursor: stepIdx === 0 ? 'not-allowed' : 'pointer',
            border: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.1)' : '#d1d5db'}`, background: 'transparent',
            color: theme === 'dark' ? '#94a3b8' : '#6b7280', opacity: stepIdx === 0 ? 0.4 : 1 }}>
          Previous
        </button>
        <button type="button" disabled={stepIdx === steps.length - 1} onClick={() => setStepIdx(stepIdx + 1)}
          style={{ padding: '6px 14px', borderRadius: '6px', fontSize: '13px', fontWeight: 600, cursor: stepIdx === steps.length - 1 ? 'not-allowed' : 'pointer',
            border: 'none', background: theme === 'dark' ? '#22c55e' : '#16a34a', color: '#fff',
            opacity: stepIdx === steps.length - 1 ? 0.4 : 1 }}>
          Next
        </button>
      </div>
    </div>
  );
};

const StepperControl = React.memo(StepperControlComponent);

// Phase 2 - Performance: Loading fallback for lazy-loaded controls
function ControlLoadingFallback({ theme }: { theme: Theme }) {
  return (
    <div style={{
      padding: '24px 16px',
      border: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.08)' : '#e5e7eb'}`,
      borderRadius: '6px',
      background: theme === 'dark' ? 'rgba(255,255,255,0.02)' : '#f9fafb',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '14px',
      color: theme === 'dark' ? '#94a3b8' : '#6b7280',
    }}>
      <span>Loading...</span>
    </div>
  );
}

function ChartPlaceholder({ type, theme }: { type: string; theme: Theme }) {
  return (
    <div style={{
      padding: '24px 16px',
      border: `2px dashed ${theme === 'dark' ? 'rgba(59,130,246,0.4)' : '#bfdbfe'}`,
      borderRadius: '8px',
      background: theme === 'dark' ? 'rgba(59,130,246,0.06)' : '#eff6ff',
      color: theme === 'dark' ? '#4ade80' : '#16a34a',
      textAlign: 'center',
      fontSize: '13px',
    }}>
      <div style={{ marginBottom: '6px' }}><BarChartIcon width={28} height={28} /></div>
      <div style={{ fontWeight: 600, textTransform: 'capitalize' }}>
        {type.replace(/-/g, ' ')}
      </div>
      <div style={{ fontSize: '11px', opacity: 0.7, marginTop: '4px' }}>
        Configure data in control settings
      </div>
    </div>
  );
}

function renderChart(control: FormControl, value: any, theme: Theme) {
  // CRITICAL: Use staticData from control.config (configured data), NOT dummy fallback
  const staticData = (control.config?.staticData as any[] | undefined) ?? [];

  // Pass full data objects to charts (they extract values as needed for labels)
  const rawData = staticData.length > 0
    ? staticData
    : ((control.config?.data as any[] | undefined)
      ?? (Array.isArray(value) ? value : null)
      ?? []);

  // For charts that need numeric arrays only
  const numericData = (Array.isArray(rawData) && rawData.length > 0)
    ? rawData.map((item: any) => typeof item === 'number' ? item : (item?.value ?? 0))
    : [];

  switch (control.type) {
    case 'bar-chart':
      return <BarChart data={rawData} theme={theme} />;
    case 'histogram':
      return <HistogramChart data={numericData} theme={theme} />;
    case 'waterfall-chart':
      return <WaterfallChart data={numericData} theme={theme} />;
    case 'line-chart':
      return <LineChart data={numericData} theme={theme} />;
    case 'area-chart':
      return <AreaChart data={numericData} theme={theme} />;
    case 'tree-map':
      return <TreeMapChart data={numericData} theme={theme} />;
    case 'pie-chart':
      return <PieChart data={rawData} theme={theme} />;
    case 'kpi-card': {
      const kpiValue = (control.config?.value as number | string) ?? value ?? 0;
      const kpiLabel = (control.config?.label as string) ?? control.label;
      return (
        <div style={{
          padding: '20px', borderRadius: '8px',
          background: theme === 'dark' ? 'rgba(59,130,246,0.12)' : '#eff6ff',
          border: `1px solid ${theme === 'dark' ? 'rgba(59,130,246,0.3)' : '#bfdbfe'}`,
        }}>
          <div style={{ fontSize: '32px', fontWeight: 700, color: theme === 'dark' ? '#4ade80' : '#16a34a' }}>
            {kpiValue}
          </div>
          <div style={{ fontSize: '13px', color: theme === 'dark' ? '#94a3b8' : '#6b7280', marginTop: '4px' }}>
            {kpiLabel}
          </div>
        </div>
      );
    }
    case 'progress-ring':
    case 'circular-gauge': {
      const pct = Math.min(100, Math.max(0, Number((control.config?.value ?? value) ?? 65)));
      const r = 40; const circ = 2 * Math.PI * r;
      return (
        <svg width={100} height={100} style={{ transform: 'rotate(-90deg)' }}>
          <circle cx={50} cy={50} r={r} fill="none"
            stroke={theme === 'dark' ? 'rgba(255,255,255,0.08)' : '#e5e7eb'} strokeWidth={10} />
          <circle cx={50} cy={50} r={r} fill="none"
            stroke={theme === 'dark' ? '#22c55e' : '#16a34a'} strokeWidth={10}
            strokeDasharray={circ} strokeDashoffset={circ * (1 - pct / 100)}
            strokeLinecap="round" />
          <text x={50} y={54} textAnchor="middle" fontSize={14} fontWeight={700}
            fill={theme === 'dark' ? '#e5e7eb' : '#111827'}
            style={{ transform: 'rotate(90deg)', transformOrigin: '50px 50px' }}>
            {pct}%
          </text>
        </svg>
      );
    }
    case 'linear-gauge':
    case 'thermometer': {
      const pct = Math.min(100, Math.max(0, Number((control.config?.value ?? value) ?? 65)));
      return (
        <div style={{ padding: '8px 0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '12px',
            color: theme === 'dark' ? '#94a3b8' : '#6b7280' }}>
            <span>0</span><span>{pct}%</span><span>100</span>
          </div>
          <div style={{ height: '12px', borderRadius: '6px', background: theme === 'dark' ? 'rgba(255,255,255,0.08)' : '#e5e7eb' }}>
            <div style={{ height: '100%', borderRadius: '6px', width: `${pct}%`,
              background: theme === 'dark' ? '#22c55e' : '#16a34a', transition: 'width 0.3s' }} />
          </div>
        </div>
      );
    }
    case 'scatter-plot': {
      const pts = (control.config?.staticData as Array<{ x: number; y: number }> | undefined)
        ?? (control.config?.data as Array<{ x: number; y: number }> | undefined)
        ?? [];
      const w = 280; const h = 160; const pad = 20;
      const xs = pts.map(p => p.x); const ys = pts.map(p => p.y);
      const minX = Math.min(...xs); const maxX = Math.max(...xs, minX + 1);
      const minY = Math.min(...ys); const maxY = Math.max(...ys, minY + 1);
      return (
        <svg width={w} height={h}>
          {pts.map((p, i) => {
            const cx = pad + ((p.x - minX) / (maxX - minX)) * (w - pad * 2);
            const cy = (h - pad) - ((p.y - minY) / (maxY - minY)) * (h - pad * 2);
            return (
              <g key={i}>
                <title>Point {i + 1}: x={p.x}, y={p.y}</title>
                <circle cx={cx} cy={cy} r={5} fill={theme === 'dark' ? '#22c55e' : '#16a34a'} opacity={0.8} />
              </g>
            );
          })}
        </svg>
      );
    }
    case 'bubble-chart': {
      const bubs = (control.config?.staticData as Array<{ x: number; y: number; z?: number }> | undefined)
        ?? (control.config?.data as Array<{ x: number; y: number; z?: number }> | undefined)
        ?? [];
      const w = 280; const h = 160; const pad = 20;
      const xs = bubs.map(p => p.x); const ys = bubs.map(p => p.y);
      const minX = Math.min(...xs); const maxX = Math.max(...xs, minX + 1);
      const minY = Math.min(...ys); const maxY = Math.max(...ys, minY + 1);
      return (
        <svg width={w} height={h}>
          {bubs.map((p, i) => {
            const cx = pad + ((p.x - minX) / (maxX - minX)) * (w - pad * 2);
            const cy = (h - pad) - ((p.y - minY) / (maxY - minY)) * (h - pad * 2);
            const r = Math.max(5, (p.z ?? 30) / 6);
            return (
              <g key={i}>
                <title>Bubble {i + 1}: x={p.x}, y={p.y}, size={p.z ?? 30}</title>
                <circle cx={cx} cy={cy} r={r}
                  fill={theme === 'dark' ? '#22c55e' : '#16a34a'} opacity={0.6}
                  stroke={theme === 'dark' ? '#4ade80' : '#15803d'} strokeWidth={1} />
              </g>
            );
          })}
        </svg>
      );
    }
    case 'heatmap':
      return <HeatmapChart data={rawData} theme={theme} />;
    case 'sankey-diagram':
      return <SankeyChart data={rawData} theme={theme} />;
    case 'network-graph':
      return <NetworkGraphChart data={rawData} theme={theme} />;
    case 'gantt-chart':
      return <GanttChart data={rawData} theme={theme} />;
    case 'analytics-dashboard': {
      const kpis = [
        { label: 'Total Users', value: rawData[0] ?? 1240, change: '+12%', up: true },
        { label: 'Revenue', value: `$${((rawData[1] ?? 84) * 100).toLocaleString()}`, change: '+8.3%', up: true },
        { label: 'Bounce Rate', value: `${rawData[2] ?? 45}%`, change: '-2.1%', up: false },
        { label: 'Avg. Session', value: `${rawData[3] ?? 90}s`, change: '+5%', up: true },
      ];
      const sparkData = rawData.length >= 5 ? rawData : [40, 70, 45, 90, 60, 80, 55];
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {/* KPI cards row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '10px' }}>
            {kpis.map((kpi, i) => (
              <div key={i} style={{
                padding: '14px', borderRadius: '8px',
                background: theme === 'dark' ? 'rgba(255,255,255,0.03)' : '#fff',
                border: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.08)' : '#e5e7eb'}`,
              }}>
                <div style={{ fontSize: '11px', color: theme === 'dark' ? '#64748b' : '#9ca3af', marginBottom: '4px' }}>{kpi.label}</div>
                <div style={{ fontSize: '22px', fontWeight: 700, color: theme === 'dark' ? '#f1f5f9' : '#111827' }}>{kpi.value}</div>
                <div style={{ fontSize: '11px', fontWeight: 600, marginTop: '4px',
                  color: kpi.up ? (theme === 'dark' ? '#4ade80' : '#16a34a') : (theme === 'dark' ? '#f87171' : '#dc2626') }}>
                  {kpi.change}
                </div>
              </div>
            ))}
          </div>
          {/* Charts row */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '10px' }}>
            {/* Line/area chart */}
            <div style={{
              padding: '14px', borderRadius: '8px',
              background: theme === 'dark' ? 'rgba(255,255,255,0.03)' : '#fff',
              border: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.08)' : '#e5e7eb'}`,
            }}>
              <div style={{ fontSize: '12px', fontWeight: 600, color: theme === 'dark' ? '#94a3b8' : '#6b7280', marginBottom: '8px' }}>Trend</div>
              <AreaChart data={sparkData} theme={theme} />
            </div>
            {/* Pie chart */}
            <div style={{
              padding: '14px', borderRadius: '8px',
              background: theme === 'dark' ? 'rgba(255,255,255,0.03)' : '#fff',
              border: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.08)' : '#e5e7eb'}`,
              display: 'flex', flexDirection: 'column', alignItems: 'center',
            }}>
              <div style={{ fontSize: '12px', fontWeight: 600, color: theme === 'dark' ? '#94a3b8' : '#6b7280', marginBottom: '8px', alignSelf: 'flex-start' }}>Distribution</div>
              <PieChart data={sparkData.slice(0, 5)} theme={theme} />
            </div>
          </div>
          {/* Bar chart row */}
          <div style={{
            padding: '14px', borderRadius: '8px',
            background: theme === 'dark' ? 'rgba(255,255,255,0.03)' : '#fff',
            border: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.08)' : '#e5e7eb'}`,
          }}>
            <div style={{ fontSize: '12px', fontWeight: 600, color: theme === 'dark' ? '#94a3b8' : '#6b7280', marginBottom: '8px' }}>Performance</div>
            <BarChart data={sparkData} theme={theme} />
          </div>
        </div>
      );
    }
    default:
      return <ChartPlaceholder type={control.type} theme={theme} />;
  }
}

// ─── QR Code Renderer (proper component to avoid hooks-in-switch-case) ─────
interface QrCodeRendererProps {
  control: FormControl;
  value: any;
  theme: Theme;
}

const QrCodeRenderer = memo(({ control, value, theme }: QrCodeRendererProps) => {
  interface QrCodeConfig {
    value?: string;
    size?: number;
    errorCorrectionLevel?: 'L' | 'M' | 'Q' | 'H';
    darkColor?: string;
    lightColor?: string;
    logoUrl?: string;
    logoSize?: number;
    enableDownload?: boolean;
    downloadFormat?: 'png' | 'jpeg' | 'svg';
    includeMargin?: boolean;
    margin?: number;
  }

  const qrConfig = (control.config as QrCodeConfig) || {};
  const qrContent = qrConfig.value || value || '';
  const qrSize = qrConfig.size ?? 200;
  const qrLevel = qrConfig.errorCorrectionLevel ?? 'M';
  const qrDarkColor = qrConfig.darkColor ?? '#000000';
  const qrLightColor = qrConfig.lightColor ?? '#FFFFFF';
  const qrIncludeMargin = qrConfig.includeMargin ?? true;
  const qrMargin = qrConfig.margin ?? 10;
  const qrEnableDownload = qrConfig.enableDownload ?? true;
  const qrDownloadRef = useRef<HTMLDivElement>(null);

  const handleQrDownload = useCallback(() => {
    if (qrDownloadRef.current) {
      const canvas = qrDownloadRef.current.querySelector('canvas') as HTMLCanvasElement;
      if (canvas) {
        const url = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.href = url;
        link.download = `qr-code-${Date.now()}.png`;
        link.click();
      }
    }
  }, []);

  if (!qrContent) {
    return (
      <div style={{
        padding: '24px', textAlign: 'center', borderRadius: '8px',
        border: `2px dashed ${theme === 'dark' ? 'rgba(255,255,255,0.2)' : '#e5e7eb'}`,
        background: theme === 'dark' ? 'rgba(255,255,255,0.03)' : '#f9fafb',
        color: theme === 'dark' ? '#94a3b8' : '#6b7280',
      }}>
        <div style={{ fontSize: '28px', marginBottom: '8px' }}>▣</div>
        <div style={{ fontSize: '13px', fontWeight: 600 }}>QR Code</div>
        <div style={{ fontSize: '12px', marginTop: '6px', opacity: 0.7 }}>No content provided</div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center' }}>
      <div ref={qrDownloadRef} style={{
        padding: qrMargin, borderRadius: '8px',
        background: qrLightColor, display: 'inline-block',
      }}>
        <QRCode
          value={qrContent}
          size={qrSize}
          level={qrLevel}
          fgColor={qrDarkColor}
          bgColor={qrLightColor}
          includeMargin={qrIncludeMargin}
        />
      </div>
      {qrEnableDownload && (
        <button
          type="button"
          onClick={handleQrDownload}
          style={{
            padding: '6px 14px', borderRadius: '6px', border: 'none',
            background: theme === 'dark' ? '#22c55e' : '#16a34a',
            color: '#fff', fontSize: '12px', fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Download QR Code
        </button>
      )}
    </div>
  );
});

QrCodeRenderer.displayName = 'QrCodeRenderer';

// ─── Barcode Renderer (proper component to avoid hooks-in-switch-case) ────
interface BarcodeRendererProps {
  control: FormControl;
  value: any;
  theme: Theme;
}

const BarcodeRenderer = memo(({ control, value, theme }: BarcodeRendererProps) => {
  interface BarcodeConfig {
    value?: string;
    format?: 'CODE128' | 'CODE39' | 'EAN13' | 'EAN8' | 'UPC' | 'ITF';
    width?: number;
    height?: number;
    displayValue?: boolean;
    fontSize?: number;
    margin?: number;
    lineColor?: string;
    backgroundColor?: string;
    enableDownload?: boolean;
    downloadFormat?: 'png' | 'jpeg' | 'svg';
  }

  const bcConfig = (control.config as BarcodeConfig) || {};
  const bcContent = bcConfig.value || value || '';
  const bcFormat = bcConfig.format ?? 'CODE128';
  const bcWidth = bcConfig.width ?? 2;
  const bcHeight = bcConfig.height ?? 100;
  const bcDisplayValue = bcConfig.displayValue ?? true;
  const bcFontSize = bcConfig.fontSize ?? 14;
  const bcMargin = bcConfig.margin ?? 10;
  const bcLineColor = bcConfig.lineColor ?? '#000000';
  const bcBackgroundColor = bcConfig.backgroundColor ?? '#FFFFFF';
  const bcEnableDownload = bcConfig.enableDownload ?? true;
  const bcSvgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (bcSvgRef.current && bcContent) {
      try {
        JsBarcode(bcSvgRef.current, bcContent, {
          format: bcFormat,
          width: bcWidth,
          height: bcHeight,
          displayValue: bcDisplayValue,
          fontSize: bcFontSize,
          margin: bcMargin,
          lineColor: bcLineColor,
          background: bcBackgroundColor,
        });
      } catch (err) {
        console.error('[FormField] Barcode generation error:', err);
      }
    }
  }, [bcContent, bcFormat, bcWidth, bcHeight, bcDisplayValue, bcFontSize, bcMargin, bcLineColor, bcBackgroundColor]);

  const handleBarcodeDownload = useCallback(() => {
    if (bcSvgRef.current) {
      const svgData = new XMLSerializer().serializeToString(bcSvgRef.current);
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const svg = new Image();

      svg.onload = () => {
        canvas.width = svg.width;
        canvas.height = svg.height;
        if (ctx) {
          ctx.fillStyle = bcBackgroundColor;
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(svg, 0, 0);
          const url = canvas.toDataURL('image/png');
          const link = document.createElement('a');
          link.href = url;
          link.download = `barcode-${Date.now()}.png`;
          link.click();
        }
      };
      svg.src = 'data:image/svg+xml;base64,' + btoa(svgData);
    }
  }, [bcBackgroundColor]);

  if (!bcContent) {
    return (
      <div style={{
        padding: '24px', textAlign: 'center', borderRadius: '8px',
        border: `2px dashed ${theme === 'dark' ? 'rgba(255,255,255,0.2)' : '#e5e7eb'}`,
        background: theme === 'dark' ? 'rgba(255,255,255,0.03)' : '#f9fafb',
        color: theme === 'dark' ? '#94a3b8' : '#6b7280',
      }}>
        <div style={{ fontSize: '28px', marginBottom: '8px' }}>║║║</div>
        <div style={{ fontSize: '13px', fontWeight: 600 }}>Barcode</div>
        <div style={{ fontSize: '12px', marginTop: '6px', opacity: 0.7 }}>No content provided</div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center' }}>
      <div style={{
        padding: bcMargin, borderRadius: '8px',
        background: bcBackgroundColor, display: 'inline-block',
      }}>
        <svg ref={bcSvgRef} />
      </div>
      {bcEnableDownload && (
        <button
          type="button"
          onClick={handleBarcodeDownload}
          style={{
            padding: '6px 14px', borderRadius: '6px', border: 'none',
            background: theme === 'dark' ? '#22c55e' : '#16a34a',
            color: '#fff', fontSize: '12px', fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Download Barcode
        </button>
      )}
    </div>
  );
});

BarcodeRenderer.displayName = 'BarcodeRenderer';

// ─── Main component ────────────────────────────────────────────────────────

export const FormField: React.FC<FormFieldProps> = ({
  control,
  value,
  error,
  touched,
  disabled,
  mode = 'edit',
  onChange,
  onBlur,
  onFocus,
  onButtonClick,
  formValues,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const theme = useFormTheme() as Theme;

  // Phase 2 - Performance: Memoize style computations to prevent recreation on every render
  const inputStyle = useMemo(() => getInputStyle(theme), [theme]);
  const labelStyle = useMemo(() => getLabelStyle(theme), [theme]);
  const fieldWrapperStyle = useMemo(() => getFieldWrapperStyle(), []);
  const helpStyle = useMemo(() => getHelpStyle(theme), [theme]);

  // Memoize commonly used inline styles
  const viewModeValueStyle = useMemo(() => ({
    padding: '9px 12px',
    borderRadius: '6px',
    background: theme === 'dark' ? 'rgba(255,255,255,0.04)' : '#f9fafb',
    border: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.08)' : '#e5e7eb'}`,
    fontSize: '14px',
    color: theme === 'dark' ? '#e5e7eb' : '#374151',
    minHeight: '38px',
    display: 'flex',
    alignItems: 'center',
  }), [theme]);

  const labelWithBoldStyle = useMemo(() => ({
    ...labelStyle,
    fontWeight: 600,
  }), [labelStyle]);

  // ── Lookup select — fetch config.optionsSource's live options (Rules of Hooks: this hook
  // must run unconditionally for every control; it's a no-op for every control that isn't a
  // `select` with `config.optionsSource` configured, i.e. every control on every other form) ──
  const optionsSource = control.type === 'select'
    ? (control.config?.optionsSource as SelectOptionsSourceConfig | undefined)
    : undefined;
  const [lookupOptions, setLookupOptions] = useState<Array<{ value: string; label: string }> | null>(null);
  useEffect(() => {
    if (!optionsSource?.endpoint || !optionsSource.valueField || !optionsSource.labelField) {
      setLookupOptions(null);
      return;
    }
    let cancelled = false;
    fetchLookupOptions(optionsSource)
      .then((opts) => { if (!cancelled) setLookupOptions(opts); })
      .catch((err) => {
        console.error(`[FormField] Failed to load lookup options for control '${control.id}':`, err);
        if (!cancelled) setLookupOptions([]);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionsSource?.endpoint, optionsSource?.method, optionsSource?.valueField, optionsSource?.labelField, control.id]);

  // ── Early returns for hidden/disabled/readonly (before any other hooks) ──
  // Get mode configuration for visibility and editability rules
  const modeConfig = getModeConfig(mode ?? 'edit');

  // If control is hidden and mode doesn't allow hidden fields, don't render anything
  if (control.hidden && !modeConfig.showHidden) {
    return null;
  }

  // Determine final disabled/readonly state based on mode config
  const isDisabled = disabled || control.disabled || !modeConfig.editable;
  const isReadonly = control.readonly || !modeConfig.editable;

  // ── Sanitizers (keep before any early return — Rules of Hooks) ───────────

  const sanitizeFieldValue = useCallback((fieldValue: any): string => {
    const str = String(fieldValue ?? '');
    // Don't sanitize email during typing — sanitizeEmail returns '' for partial emails
    // (e.g. "john@gm" fails the regex). Validation handles correctness on submit.
    // Don't sanitize textarea/code fields — they hold code/expressions/JSON where quotes
    // and special chars must be preserved literally. HTML-encoding breaks stored values.
    switch (control.type) {
      case 'email': return str;
      case 'textarea': return str;
      case 'code-editor': return str;
      case 'enhanced-json-editor': return str;
      case 'json-editor': return str;
      default: return sanitizeInput(str, { strict: false });
    }
  }, [control.type]);

  const displayValue = useMemo(() => sanitizeFieldValue(value), [value, sanitizeFieldValue]);

  const sanitizedError = useMemo(() => error ? stripHtml(error) : undefined, [error]);
  const sanitizedHelp = useMemo(() => control.helpText ? stripHtml(control.helpText) : undefined, [control.helpText]);
  const sanitizedLabel = useMemo(() => control.label ? stripHtml(control.label) : undefined, [control.label]);

  const showError = touched && sanitizedError;
  const errorId = `${control.id}-error`;
  const helpId = control.helpText ? `${control.id}-help` : undefined;

  const describedBy = useMemo(() => {
    const ids: string[] = [];
    if (showError) ids.push(errorId);
    if (helpId) ids.push(helpId);
    return ids.length ? ids.join(' ') : undefined;
  }, [showError, errorId, helpId]);

  // ── handleChange must be declared before any early return (Rules of Hooks) ──
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    onChange?.(sanitizeFieldValue(e.target.value));
  }, [sanitizeFieldValue, onChange]);

  // ── Design mode — dashed outline + type badge, interactive controls inside ──
  if (mode === 'design') {
    return (
      <div style={{ position: 'relative', outline: '1px dashed rgba(59,130,246,0.5)', borderRadius: 6, padding: 2 }}>
        <span style={{
          position: 'absolute', top: 4, right: 4, zIndex: 10,
          padding: '2px 7px', borderRadius: 4, fontSize: 10, fontWeight: 700,
          background: 'rgba(59,130,246,0.85)', color: '#fff', pointerEvents: 'none',
        }}>
          {control.type}
        </span>
        {/* Render normal edit controls inside the design wrapper */}
        <FormField {...{ control, value, error, touched, disabled, onChange, onBlur, onFocus }} mode="edit" />
      </div>
    );
  }

  // ── Admin mode — purple outline + badge, all fields editable ─────────────
  if (mode === 'admin') {
    return (
      <div style={{
        position: 'relative',
        outline: theme === 'dark' ? '1px solid rgba(168,85,247,0.45)' : '1px solid rgba(124,58,237,0.35)',
        borderRadius: 6, padding: 2,
      }}>
        <span style={{
          position: 'absolute', top: 4, right: 4, zIndex: 10,
          padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 700,
          background: theme === 'dark' ? 'rgba(168,85,247,0.85)' : 'rgba(124,58,237,0.85)',
          color: '#fff', pointerEvents: 'none',
        }}>
          admin
        </span>
        {/* Render normal edit controls — admin sees everything editable */}
        <FormField {...{ control, value, error, touched, disabled, onChange, onBlur, onFocus }} mode="edit" />
      </div>
    );
  }

  // ── Preview mode — read-only display with labels and help text ──
  if (mode === 'preview') {
    const displayVal = (() => {
      if (value === null || value === undefined || value === '') return null;
      if (typeof value === 'boolean') return value ? 'Yes' : 'No';
      if (Array.isArray(value)) return value.join(', ');
      return String(value);
    })();
    return (
      <div style={fieldWrapperStyle}>
        <label style={labelWithBoldStyle}>
          {sanitizedLabel}
          {control.required && modeConfig.showRequired && (
            <span style={{ color: '#ef4444', marginLeft: '3px' }}>*</span>
          )}
        </label>
        <div style={viewModeValueStyle}>
          {displayVal ?? <span style={{ opacity: 0.4 }}>—</span>}
        </div>
        {sanitizedHelp && modeConfig.showHelpText && (
          <p style={helpStyle}>{sanitizedHelp}</p>
        )}
      </div>
    );
  }

  // ── View mode ─────────────────────────────────────────────────────────────
  if (mode === 'view') {
    // Complex layout controls, and every stateless display-only control type, render
    // themselves identically regardless of mode — let them all fall through to the same
    // "Display controls" branch further down that mode="edit" already uses (real bug found
    // live, 2026-09-10: only 'image'/'video' were special-cased here; every OTHER DISPLAY_TYPES
    // member — label, header, article, html, css, audio, iframe-viewer, pdf-viewer, mermaid —
    // fell into the generic value-display branch below, which shows the control's bound
    // `value` and is meaningless for a display-only control that has no value, only
    // `config.content`/`config.src` — so those controls silently rendered a blank "—"
    // in mode="view" while rendering correctly in mode="edit". This is exactly why Form
    // Studio's "View" (mode="view") and "Open" (mode="edit") actions looked like two
    // different, divergent rendering engines when they're really the same one with an
    // incomplete early-return here.
    if (control.type === 'form-container' || control.type === 'grid' || DISPLAY_TYPES.has(control.type)) {
      // Fall through to the normal switch/case below
      // grid: renders GridControl with buttons disabled (read-only via bulkEditEnabled=false)
      // form-container: handles mode="view" by setting child forms read-only
      // DISPLAY_TYPES: no edit/view distinction applies to a control with no bound value
    } else if (CHART_TYPES.has(control.type)) {
      return (
        <div style={fieldWrapperStyle}>
          <label style={labelStyle}>{sanitizedLabel}</label>
          {renderChart(control, value, theme)}
        </div>
      );
    } else if (control.type === 'file-upload') {
      // File-upload control in view mode: show downloadable files
      return (
        <div style={fieldWrapperStyle}>
          <FileUploadViewMode
            value={value}
            label={sanitizedLabel ?? ''}
            multiple={control.config?.multiple === true}
            theme={theme}
          />
        </div>
      );
    } else {
      // Compute display label for select/radio types
      let viewDisplayValue: string | null = null;
      if ((control.type === 'select' || control.type === 'radio') && value) {
        const opts = ((control.config?.options ?? (control as any).options) as Array<{ value: string; label: string }> | undefined) ?? [];
        const found = opts.find(o => o.value === String(value));
        viewDisplayValue = found?.label ?? String(value);
      } else if (control.type === 'checkbox' || control.type === 'switch') {
        viewDisplayValue = value ? 'Yes' : 'No';
      } else if (control.type === 'multiselect' && Array.isArray(value)) {
        const opts = ((control.config?.options ?? (control as any).options) as Array<{ value: string; label: string }> | undefined) ?? [];
        viewDisplayValue = (value as string[])
          .map(v => opts.find(o => o.value === v)?.label ?? v)
          .join(', ');
      } else if ((control.type === 'date' || control.type === 'datetime') && value) {
        try {
          viewDisplayValue = new Intl.DateTimeFormat(undefined, {
            dateStyle: 'medium',
            timeStyle: control.type === 'datetime' ? 'short' : undefined,
          }).format(new Date(String(value)));
        } catch { viewDisplayValue = String(value); }
      } else {
        viewDisplayValue = value !== null && value !== undefined ? String(value) : null;
      }
      return (
        <div style={fieldWrapperStyle}>
          <label style={labelWithBoldStyle}>{sanitizedLabel}</label>
          <div style={viewModeValueStyle}>
            {viewDisplayValue ?? <span style={{ opacity: 0.4 }}>—</span>}
          </div>
        </div>
      );
    }
  }

  // ── Chart controls — render visualization in edit/preview mode ─────────────
  if (CHART_TYPES.has(control.type)) {
    return (
      <div style={fieldWrapperStyle}>
        <label style={labelStyle}>{sanitizedLabel}</label>
        {renderChart(control, value, theme)}
        {sanitizedHelp && <p style={helpStyle}>{sanitizedHelp}</p>}
      </div>
    );
  }

  // ── Display controls ───────────────────────────────────────────────────────
  if (DISPLAY_TYPES.has(control.type)) {
    if (control.type === 'header') {
      // Real bug found live, 2026-09-10: this previously read only `sanitizedLabel` (=
      // control.label), never `config.content` — contradicting header.md's own documented
      // contract ("content, falls back to label"). A header authored with only `config.content`
      // set (the normal, documented way to author one) rendered completely blank.
      const headerContent = (control.config?.content as string | undefined) ?? sanitizedLabel;
      // `level` is documented (header.md) as a number 1-6 (default 2), "accepts a numeric
      // string too" — this previously compared it against the literal strings 'h1'..'h6', so a
      // real numeric `level` (the only form the doc actually describes) never matched and
      // silently fell back to 'h3' every time.
      const levelRaw = control.config?.level;
      const levelNum = Math.min(6, Math.max(1, Number(levelRaw) || 2));
      const tag = `h${levelNum}` as keyof JSX.IntrinsicElements;
      const fontSize = levelNum === 1 ? '22px' : levelNum === 2 ? '18px' : levelNum === 4 ? '14px' : '15px';
      const Tag = tag;
      const alignment = control.config?.alignment as string | undefined;
      const color = (control.config?.color as string | undefined) ?? (theme === 'dark' ? '#f8fafc' : '#111827');
      return <Tag style={{ margin: '16px 0 6px', fontSize, fontWeight: 700, color, textAlign: alignment as any }}>{headerContent}</Tag>;
    }
    if (control.type === 'label') {
      // Same real bug as 'header' above: label.md documents "content, falls back to label,
      // then description, then defaultValue" — this previously skipped `config.content`
      // entirely and started the fallback chain at `label`.
      const labelText = (control.config?.content as string | undefined)
        ?? sanitizedLabel
        ?? (control.description ? stripHtml(control.description) : undefined)
        ?? (typeof control.defaultValue === 'string' ? control.defaultValue : undefined);
      const fontSizeMap: Record<string, string> = { sm: '12px', md: '14px', lg: '16px', xl: '20px' };
      const fontSize = fontSizeMap[(control.config?.fontSize as string) ?? 'md'] ?? '14px';
      const fontWeight = (control.config?.fontWeight as string) ?? 'normal';
      const alignment = control.config?.alignment as string | undefined;
      const color = (control.config?.color as string | undefined) ?? (theme === 'dark' ? '#94a3b8' : '#6b7280');
      return <p style={{ margin: '0 0 12px', fontSize, fontWeight, color, textAlign: alignment as any }}>{labelText}</p>;
    }
    if (control.type === 'image') {
      return <ImageControl control={control} value={value} onChange={onChange ?? (() => {})} onBlur={onBlur ?? (() => {})} />;
    }
    if (control.type === 'html') {
      return (
        <Suspense fallback={<ControlLoadingFallback theme={theme} />}>
          <HtmlControl control={control} value={value} onChange={onChange ?? (() => {})} onBlur={onBlur ?? (() => {})} />
        </Suspense>
      );
    }
    if (control.type === 'video') {
      return (
        <Suspense fallback={<ControlLoadingFallback theme={theme} />}>
          <VideoControl control={control} value={value} onChange={onChange ?? (() => {})} onBlur={onBlur ?? (() => {})} />
        </Suspense>
      );
    }
    if (control.type === 'audio') {
      return (
        <Suspense fallback={<ControlLoadingFallback theme={theme} />}>
          <AudioControl control={control} value={value} onChange={onChange ?? (() => {})} onBlur={onBlur ?? (() => {})} />
        </Suspense>
      );
    }
    if (control.type === 'iframe-viewer') {
      return (
        <Suspense fallback={<ControlLoadingFallback theme={theme} />}>
          <IframeViewerControl control={control} value={value} onChange={onChange ?? (() => {})} onBlur={onBlur ?? (() => {})} />
        </Suspense>
      );
    }
    if (control.type === 'pdf-viewer') {
      return (
        <Suspense fallback={<ControlLoadingFallback theme={theme} />}>
          <PdfViewerControl control={control} value={value} onChange={onChange ?? (() => {})} onBlur={onBlur ?? (() => {})} />
        </Suspense>
      );
    }
    if (control.type === 'article') {
      return (
        <Suspense fallback={<ControlLoadingFallback theme={theme} />}>
          <ArticleControl control={control} value={value} onChange={onChange ?? (() => {})} onBlur={onBlur ?? (() => {})} />
        </Suspense>
      );
    }
    if (control.type === 'mermaid') {
      return (
        <Suspense fallback={<ControlLoadingFallback theme={theme} />}>
          <MermaidControl control={control} value={value} onChange={onChange ?? (() => {})} onBlur={onBlur ?? (() => {})} />
        </Suspense>
      );
    }
    if (control.type === 'css') {
      const rawCss = (control.config?.css as string) || (control as any).content || (typeof value === 'string' ? value : '');
      // Sanitize CSS: strip dangerous functions, protocols, and properties
      const cssContent = rawCss
        .replace(/@import\b[^;]*/gi, '/* removed */')
        .replace(/@charset\b[^;]*/gi, '/* removed */')
        .replace(/expression\s*\(/gi, '/* removed */(')
        .replace(/javascript\s*:/gi, '/* removed */')
        .replace(/-moz-binding\s*:/gi, '/* removed */:')
        .replace(/behavior\s*:/gi, '/* removed */:')
        .replace(/url\s*\(/gi, '/* url removed */(')
        .replace(/<\/?[a-z][^>]*>/gi, '')
        .replace(/\\[0-9a-f]{1,6}/gi, '');
      return (
        <div style={fieldWrapperStyle}>
          {cssContent && <style dangerouslySetInnerHTML={{ __html: cssContent }} />}
          {sanitizedLabel && (
            <p style={{ fontSize: '12px', color: theme === 'dark' ? '#94a3b8' : '#6b7280', margin: 0 }}>
              CSS: {sanitizedLabel}
            </p>
          )}
        </div>
      );
    }
    return (
      <div style={{ ...fieldWrapperStyle, padding: '10px 12px', background: theme === 'dark' ? 'rgba(255,255,255,0.04)' : '#f9fafb',
        borderRadius: '6px', border: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.08)' : '#e5e7eb'}`,
        fontSize: '13px', color: theme === 'dark' ? '#94a3b8' : '#6b7280' }}>
        [{control.type}] {sanitizedLabel}
      </div>
    );
  }

  // ── Input controls (copied from atlas-form-player FormField pattern) ────────

  const strValue = displayValue;

  const renderControl = () => {
    // ── Inheritance error placeholder ──────────────────────────────────────
    if ((control.type as unknown) === '_inheritance-error') {
      const msg = (control as unknown as Record<string, unknown>)._errorMessage as string | undefined;
      return (
        <div style={{
          padding: '10px 14px', borderRadius: 6, fontSize: 13,
          background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)',
          color: '#ef4444',
        }}>
          <strong>Could not load inherited control</strong>
          {msg ? `: ${msg}` : ''}
        </div>
      );
    }

    switch (control.type) {
      case 'text':
      case 'email':
      case 'url':
      case 'tel': {
        // Unchanged from before Plan/05 §3B — every attribute, in the same order. Extracted to a
        // const only so the opt-in branch below can render the *same element* rather than a copy.
        const nativeInput = (
          <input
            id={control.id}
            type={control.type === 'tel' ? 'tel' : control.type}
            style={{ ...inputStyle, borderColor: showError ? '#ef4444' : undefined }}
            value={strValue}
            placeholder={control.placeholder}
            disabled={isDisabled}
            readOnly={control.readonly}
            required={control.required}
            maxLength={control.config?.maxLength as number | undefined ?? control.validation?.maxLength as number | undefined}
            pattern={control.validation?.pattern}
            autoComplete={control.config?.autoComplete as string | undefined ?? (control.type === 'email' ? 'email' : control.type === 'tel' ? 'tel' : undefined)}
            onChange={handleChange}
            onBlur={onBlur}
            onFocus={onFocus}
            aria-invalid={showError ? 'true' : 'false'}
            aria-required={control.required ? 'true' : 'false'}
            aria-describedby={describedBy}
          />
        );

        /**
         * Plan/05 §3B — opt-in "Edit using Expression Builder", off unless a field explicitly sets
         * `config.expressionBuilder.enabled: true`.
         *
         * The early return is the point: with the flag absent or false — which is every field on
         * every form that exists today — this case returns the bare `<input>` it always returned,
         * with no wrapper element and no changed attribute. `FormField.expressionBuilder.test.tsx`
         * asserts that byte-for-byte against a rendered snapshot.
         *
         * `control.type === 'text'` gates the other three types out. `email`/`url`/`tel` render
         * through this same case and are conventionally atomic values, not template content
         * (Plan/05 §3B's stated default position), so the toggle does nothing for them even if a
         * schema sets it.
         */
        if (control.type !== 'text' || !isExpressionBuilderEnabled(control)) {
          return nativeInput;
        }

        return (
          <div className="af-expr-field-row" style={{ display: 'flex', alignItems: 'center', gap: '6px', width: '100%' }}>
            {nativeInput}
            {/*
              The boundary, not just the `Suspense`. `Suspense` catches the lazy chunk's *pending*
              state; a **rejected** import throws during render, unwinds past this whole case and is
              caught by `FormRenderer`'s `FieldErrorBoundary`, which replaces the entire field —
              input, label, value — with "Field error". A failed fetch of an optional convenience
              button must not cost the user their field, so it is caught one level in and degrades
              to a disabled trigger.
            */}
            <ExpressionBuilderErrorBoundary controlID={control.id} theme={theme}>
              <Suspense fallback={EXPRESSION_TRIGGER_SUSPENSE_FALLBACK}>
                <ExpressionBuilderTrigger
                  control={control}
                  value={strValue}
                  // Same path a keystroke takes, in the same order: the native `maxLength` attribute
                  // caps `e.target.value` first and `handleChange` sanitises what survives, so
                  // `applyMaxLength` runs before `sanitizeFieldValue` here. Writing `onChange(next)`
                  // raw would let the builder bypass both the XSS filter and the length limit this
                  // field applies to everything else it accepts.
                  onConfirm={(next) => onChange?.(sanitizeFieldValue(applyMaxLength(next, control)))}
                  disabled={isDisabled || control.readonly === true}
                  theme={theme}
                  defaultOutputFormat={TEXT_EXPRESSION_DEFAULT_FORMAT}
                />
              </Suspense>
            </ExpressionBuilderErrorBoundary>
          </div>
        );
      }

      case 'password':
        return (
          <div style={{ position: 'relative' }}>
            <input
              id={control.id}
              type={showPassword ? 'text' : 'password'}
              style={{ ...inputStyle, paddingRight: '42px', borderColor: showError ? '#ef4444' : undefined }}
              value={strValue}
              placeholder={control.placeholder}
              disabled={isDisabled}
              readOnly={control.readonly}
              required={control.required}
              autoComplete={control.config?.autoComplete as string | undefined ?? 'current-password'}
              onChange={handleChange}
              onBlur={onBlur}
              onFocus={onFocus}
              aria-invalid={showError ? 'true' : 'false'}
              aria-required={control.required ? 'true' : 'false'}
              aria-describedby={describedBy}
            />
            <button type="button" tabIndex={-1}
              onClick={() => setShowPassword(p => !p)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                color: theme === 'dark' ? '#94a3b8' : '#6b7280', display: 'flex', alignItems: 'center' }}>
              {showPassword ? <EyeOffIcon width={16} height={16} /> : <EyeIcon width={16} height={16} />}
            </button>
          </div>
        );

      case 'number':
        return (
          <input
            id={control.id}
            type="number"
            style={{ ...inputStyle, borderColor: showError ? '#ef4444' : undefined }}
            value={strValue}
            placeholder={control.placeholder}
            disabled={isDisabled}
            readOnly={control.readonly}
            min={control.validation?.min}
            max={control.validation?.max}
            step={control.config?.step as number | string | undefined ?? 'any'}
            required={control.required}
            onChange={(e) => onChange?.(e.target.valueAsNumber)}
            onBlur={onBlur}
            onFocus={onFocus}
            aria-invalid={showError ? 'true' : 'false'}
            aria-required={control.required ? 'true' : 'false'}
            aria-describedby={describedBy}
          />
        );

      case 'textarea': {
        // Unchanged from before Plan/05 §3B — every attribute, in the same order.
        const nativeTextarea = (
          <textarea
            id={control.id}
            style={{ ...inputStyle, height: '100px', resize: 'vertical', borderColor: showError ? '#ef4444' : undefined }}
            value={strValue}
            placeholder={control.placeholder}
            disabled={isDisabled}
            readOnly={control.readonly}
            required={control.required}
            rows={control.config?.rows as number | undefined ?? 4}
            maxLength={control.config?.maxLength as number | undefined ?? control.validation?.maxLength as number | undefined}
            onChange={handleChange}
            onBlur={onBlur}
            onFocus={onFocus}
            aria-invalid={showError ? 'true' : 'false'}
            aria-required={control.required ? 'true' : 'false'}
            aria-describedby={describedBy}
          />
        );

        /** Plan/05 §3B, same contract as `case 'text':` above — see that block's note. */
        if (!isExpressionBuilderEnabled(control)) {
          return nativeTextarea;
        }

        return (
          // `flex-start` rather than `center`: a textarea is 100px tall and the trigger should sit
          // at its top edge, level with the first line, not floating halfway down the box.
          <div className="af-expr-field-row" style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', width: '100%' }}>
            {nativeTextarea}
            {/* Error boundary before `Suspense`, for the reason spelled out in `case 'text':`. */}
            <ExpressionBuilderErrorBoundary controlID={control.id} theme={theme}>
              <Suspense fallback={EXPRESSION_TRIGGER_SUSPENSE_FALLBACK}>
                <ExpressionBuilderTrigger
                  control={control}
                  value={strValue}
                  // `sanitizeFieldValue` is a pass-through for `textarea` (it holds code/expressions
                  // /JSON where quotes must survive literally), so this is still exactly the path a
                  // keystroke takes — routing through it keeps the two entry points identical rather
                  // than relying on today's switch never gaining a `textarea` branch. `applyMaxLength`
                  // is the `maxLength` attribute this element carries, applied to the one path an
                  // attribute cannot reach.
                  onConfirm={(next) => onChange?.(sanitizeFieldValue(applyMaxLength(next, control)))}
                  disabled={isDisabled || control.readonly === true}
                  theme={theme}
                  defaultOutputFormat={TEXT_EXPRESSION_DEFAULT_FORMAT}
                />
              </Suspense>
            </ExpressionBuilderErrorBoundary>
          </div>
        );
      }

      case 'select': {
        // Lookup select (config.optionsSource): once real options load, they used to
        // completely REPLACE config.options -- including any author-authored "clear this
        // filter" entry (e.g. `{ label: "Any", value: "" }`), which the fetched list from a
        // real API never includes itself. That silently removed the only way to deselect a
        // lookup select back to "no filter" once a real value had been chosen (the disabled
        // placeholder <option> below isn't user-selectable). Fixed by keeping any
        // empty-value ("") entries from the static config.options as a real, always-present
        // prefix ahead of the fetched options, instead of discarding config.options outright
        // once lookupOptions is loaded. Falls back to the plain static list while loading (or
        // when no optionsSource is configured at all) -- unchanged, backward-compatible path
        // for every other select control.
        const staticOpts = (control.config?.options ?? (control as any).options) as Array<{ value: string; label: string }> | undefined;
        const clearOpts = staticOpts?.filter((o) => o.value === '') ?? [];
        const selectOpts = lookupOptions ? [...clearOpts, ...lookupOptions] : staticOpts;
        const isLookupLoading = !!optionsSource?.endpoint && lookupOptions === null;
        // Only show the synthetic placeholder when the resolved list has no real empty-value
        // ("clear") entry of its own -- otherwise the dropdown would show two blank-looking
        // "no selection" rows (the placeholder and the real "Any" entry).
        const hasRealClearOption = (selectOpts ?? []).some((o) => o.value === '');
        // Optional generic capability: a select can opt into immediately re-running the
        // form's 'search'-triggered apiAction on change (e.g. a page-size selector), via the
        // exact same onButtonClick mechanism a type:"button" Search control already uses.
        // `resetControlID`/`resetValue` are a small paired capability: when changing this
        // select should also reset another control (e.g. paging back to page 1 whenever the
        // page size changes, since the current page number may no longer be in range).
        const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
          handleChange(e);
          if (control.config?.triggerSearchOnChange) {
            const nextValues: Record<string, any> = { ...(formValues || {}), [control.id]: sanitizeFieldValue(e.target.value) };
            const resetControlID = control.config?.resetControlID as string | undefined;
            if (resetControlID) {
              nextValues[resetControlID] = control.config?.resetValue ?? 1;
            }
            onButtonClick?.(control.id, nextValues);
          }
        };
        // Optional generic capability: config.maxWidth caps this select's rendered width
        // (e.g. a compact page-size picker) instead of the default 100%-of-container from
        // inputStyle. Omitted for every other select control today, so behavior there is
        // unchanged.
        const selectMaxWidth = control.config?.maxWidth as string | undefined;
        return (
          <select
            id={control.id}
            style={{
              ...inputStyle,
              borderColor: showError ? '#ef4444' : undefined,
              ...(selectMaxWidth ? { width: 'auto', maxWidth: selectMaxWidth } : {}),
            }}
            value={strValue}
            disabled={isDisabled || isLookupLoading}
            required={control.required}
            onChange={handleSelectChange}
            onBlur={onBlur}
            aria-invalid={showError ? 'true' : 'false'}
            aria-required={control.required ? 'true' : 'false'}
            aria-describedby={describedBy}
          >
            {!hasRealClearOption && (
              <option value="" disabled={control.required}>{isLookupLoading ? 'Loading…' : 'Select...'}</option>
            )}
            {selectOpts?.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        );
      }

      case 'radio': {
        const opts = ((control.config?.options ?? (control as any).options) as Array<{ value: string; label: string }> | undefined) ?? [];
        return (
          <div role="radiogroup" aria-label={sanitizedLabel} aria-required={control.required ? 'true' : 'false'}
            style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            {opts.map((opt) => (
              <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: isDisabled ? 'not-allowed' : 'pointer', fontSize: '14px' }}>
                <input type="radio" name={control.id} value={opt.value}
                  checked={String(value ?? '') === opt.value}
                  disabled={isDisabled}
                  onChange={() => onChange?.(opt.value)}
                  onBlur={onBlur}
                  aria-invalid={showError ? 'true' : 'false'}
                  style={{ width: '15px', height: '15px' }} />
                {opt.label}
              </label>
            ))}
            {opts.length === 0 && <span style={{ fontSize: '12px', opacity: 0.5 }}>No options configured</span>}
          </div>
        );
      }

      case 'checkbox': {
        const opts = ((control.config?.options ?? (control as any).options) as Array<{ value: string; label: string }> | undefined) ?? [];
        if (opts.length === 0) {
          return (
            <input type="checkbox" checked={Boolean(value)} disabled={isDisabled}
              onChange={(e) => onChange?.(e.target.checked)}
              onBlur={onBlur}
              aria-invalid={showError ? 'true' : 'false'}
              aria-required={control.required ? 'true' : 'false'}
              style={{ width: '16px', height: '16px', cursor: isDisabled ? 'not-allowed' : 'pointer' }} />
          );
        }
        const selected = Array.isArray(value) ? (value as string[]) : [];
        return (
          <div role="group" aria-label={sanitizedLabel} aria-required={control.required ? 'true' : 'false'}
            style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            {opts.map((opt) => (
              <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: isDisabled ? 'not-allowed' : 'pointer', fontSize: '14px' }}>
                <input type="checkbox" checked={selected.includes(opt.value)} disabled={isDisabled}
                  onChange={(e) => {
                    onChange?.(e.target.checked
                      ? [...selected, opt.value]
                      : selected.filter(v => v !== opt.value));
                  }}
                  onBlur={onBlur}
                  aria-invalid={showError ? 'true' : 'false'}
                  style={{ width: '15px', height: '15px' }} />
                {opt.label}
              </label>
            ))}
          </div>
        );
      }

      case 'switch': {
        const isOn = Boolean(value);
        const onLabel = (control.config?.onLabel as string) || 'On';
        const offLabel = (control.config?.offLabel as string) || 'Off';
        return (
          <button type="button" role="switch" aria-checked={isOn} aria-label={sanitizedLabel} disabled={isDisabled}
            onClick={() => onChange?.(!isOn)}
            style={{
              display: 'inline-flex', alignItems: 'center',
              background: 'none', border: 'none', cursor: isDisabled ? 'not-allowed' : 'pointer',
              padding: 0,
            }}>
            <span style={{
              width: '56px', height: '24px', borderRadius: '12px', position: 'relative',
              background: isOn ? '#22c55e' : (theme === 'dark' ? '#374151' : '#d1d5db'),
              transition: 'background 0.2s', display: 'inline-flex', alignItems: 'center',
              flexShrink: 0,
            }}>
              {/* On/Off text inside the pill, opposite side from thumb */}
              <span style={{
                position: 'absolute',
                left: isOn ? '6px' : undefined,
                right: isOn ? undefined : '6px',
                fontSize: '9px', fontWeight: 700, letterSpacing: '0.02em',
                color: '#fff', userSelect: 'none', lineHeight: 1,
                transition: 'left 0.2s, right 0.2s',
              }}>
                {isOn ? onLabel : offLabel}
              </span>
              {/* Thumb */}
              <span style={{
                position: 'absolute', top: '3px',
                left: isOn ? '35px' : '3px',
                width: '18px', height: '18px', borderRadius: '50%',
                background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
              }} />
            </span>
          </button>
        );
      }

      case 'date':
        return (
          <input id={control.id} type="date"
            style={{ ...inputStyle, borderColor: showError ? '#ef4444' : undefined }}
            value={String(value ?? '')} disabled={isDisabled} required={control.required}
            onChange={(e) => onChange?.(e.target.value)} onBlur={onBlur}
            aria-invalid={showError ? 'true' : 'false'}
            aria-required={control.required ? 'true' : 'false'}
            aria-describedby={describedBy} />
        );

      case 'datetime':
        return (
          <input id={control.id} type="datetime-local"
            style={{ ...inputStyle, borderColor: showError ? '#ef4444' : undefined }}
            value={String(value ?? '')} disabled={isDisabled} required={control.required}
            onChange={(e) => onChange?.(e.target.value)} onBlur={onBlur}
            aria-invalid={showError ? 'true' : 'false'}
            aria-required={control.required ? 'true' : 'false'}
            aria-describedby={describedBy} />
        );

      case 'json-editor': {
        const jsonStr = typeof value === 'object' ? JSON.stringify(value, null, 2) : strValue;
        const isValidJson = (() => { try { JSON.parse(typeof value === 'string' ? value : '{}'); return true; } catch { return typeof value !== 'string'; } })();
        return (
          <textarea
            id={control.id}
            style={{
              ...inputStyle, height: '160px', fontFamily: 'monospace', fontSize: '12px', resize: 'vertical',
              borderColor: !isValidJson ? '#f59e0b' : showError ? '#ef4444' : undefined,
            }}
            value={jsonStr}
            placeholder="{}"
            disabled={isDisabled}
            onChange={(e) => {
              try { onChange?.(JSON.parse(e.target.value)); }
              catch { onChange?.(e.target.value); }
            }}
            onBlur={onBlur}
            aria-invalid={showError || !isValidJson ? 'true' : 'false'}
            aria-describedby={describedBy}
          />
        );
      }

      case 'multiselect': {
        const msOpts = (control.config?.options as Array<{ value: string; label: string }> | undefined) ?? [];
        const msSelected = Array.isArray(value) ? (value as string[]) : (value ? [String(value)] : []);
        const [msOpen, setMsOpen] = React.useState(false);
        const msRef = React.useRef<HTMLDivElement>(null);

        const msAvailable = msOpts.filter(o => !msSelected.includes(o.value));

        // Close on click outside
        React.useEffect(() => {
          if (!msOpen) return;
          const handler = (e: MouseEvent) => { if (msRef.current && !msRef.current.contains(e.target as Node)) setMsOpen(false); };
          document.addEventListener('mousedown', handler);
          return () => document.removeEventListener('mousedown', handler);
        }, [msOpen]);

        return (
          <div ref={msRef} style={{ position: 'relative' }}>
            {/* Selected tags + toggle */}
            <div
              onClick={() => { if (!isDisabled) setMsOpen(!msOpen); }}
              style={{
                ...inputStyle, borderColor: showError ? '#ef4444' : msOpen ? (theme === 'dark' ? '#22c55e' : '#16a34a') : undefined,
                display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center',
                cursor: isDisabled ? 'not-allowed' : 'pointer', minHeight: '40px', padding: '6px 10px',
              }}
              aria-invalid={showError ? 'true' : 'false'}
              aria-required={control.required ? 'true' : 'false'}
              aria-describedby={describedBy}
            >
              {msSelected.length === 0 && (
                <span style={{ opacity: 0.5, fontSize: 13 }}>Select...</span>
              )}
              {msSelected.map((v) => {
                const opt = msOpts.find(o => o.value === v);
                return (
                  <span key={v} style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '12px',
                    background: theme === 'dark' ? 'rgba(34,197,94,0.15)' : '#dcfce7', color: theme === 'dark' ? '#4ade80' : '#15803d',
                    display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
                    {opt?.label ?? v}
                    {!isDisabled && <button type="button" onClick={(e) => { e.stopPropagation(); onChange?.(msSelected.filter(x => x !== v)); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: '13px', color: 'inherit', lineHeight: 1 }}><CloseIcon width={11} height={11} /></button>}
                  </span>
                );
              })}
              <span style={{ marginLeft: 'auto', fontSize: 10, color: '#999', flexShrink: 0 }}>{msOpen ? <ChevronUpIcon width={10} height={10} /> : <ChevronDownIcon width={10} height={10} />}</span>
            </div>

            {/* Dropdown */}
            {msOpen && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, zIndex: 100,
                background: theme === 'dark' ? '#1e293b' : '#fff',
                border: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.15)' : '#d1d5db'}`,
                borderRadius: 6, boxShadow: '0 4px 16px rgba(0,0,0,0.15)', maxHeight: 200, overflowY: 'auto',
              }}>
                {msAvailable.length === 0 && (
                  <div style={{ padding: '12px', textAlign: 'center', fontSize: 13, color: '#999' }}>
                    {msOpts.length === 0 ? 'No options configured' : 'All options selected'}
                  </div>
                )}
                {msAvailable.map((opt) => (
                  <div key={opt.value}
                    onClick={() => { onChange?.([...msSelected, opt.value]); }}
                    style={{ padding: '8px 12px', fontSize: '13px', cursor: 'pointer',
                      color: theme === 'dark' ? '#e2e8f0' : '#374151',
                      display: 'flex', alignItems: 'center', gap: 8,
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = theme === 'dark' ? 'rgba(34,197,94,0.1)' : '#f0fdf4'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <span style={{ width: 14, height: 14, borderRadius: 3, border: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.2)' : '#d1d5db'}`, flexShrink: 0 }} />
                    {opt.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      }

      case 'color-picker':
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <input
              id={control.id}
              type="color"
              value={String(value || '#000000')}
              disabled={isDisabled}
              onChange={(e) => onChange?.(e.target.value)}
              onBlur={onBlur}
              aria-label={sanitizedLabel}
              aria-describedby={describedBy}
              style={{ width: '48px', height: '36px', padding: '2px', cursor: isDisabled ? 'not-allowed' : 'pointer',
                border: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.15)' : '#d1d5db'}`,
                borderRadius: '4px', background: 'none' }}
            />
            <span style={{ fontSize: '13px', fontFamily: 'monospace', color: theme === 'dark' ? '#94a3b8' : '#6b7280' }}>
              {String(value || '#000000')}
            </span>
          </div>
        );

      case 'slider-range': {
        const srMin = Number((control.config?.min ?? (control.validation as any)?.min) ?? 0);
        const srMax = Number((control.config?.max ?? (control.validation as any)?.max) ?? 100);
        const srStep = Number((control.config?.step) ?? 1);
        const srVal = Number(value ?? srMin);
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: theme === 'dark' ? '#94a3b8' : '#6b7280' }}>
              <span>{srMin}</span>
              <span style={{ fontWeight: 600, color: theme === 'dark' ? '#e2e8f0' : '#374151' }}>{srVal}</span>
              <span>{srMax}</span>
            </div>
            <input
              id={control.id}
              type="range"
              min={srMin}
              max={srMax}
              step={srStep}
              value={srVal}
              disabled={isDisabled}
              onChange={(e) => onChange?.(Number(e.target.value))}
              onBlur={onBlur}
              aria-label={sanitizedLabel}
              aria-valuemin={srMin}
              aria-valuemax={srMax}
              aria-valuenow={srVal}
              aria-describedby={describedBy}
              style={{ width: '100%', cursor: isDisabled ? 'not-allowed' : 'pointer', accentColor: '#22c55e' }}
            />
          </div>
        );
      }

      case 'time-picker':
        return (
          <input
            id={control.id}
            type="time"
            style={{ ...inputStyle, borderColor: showError ? '#ef4444' : undefined }}
            value={String(value ?? '')}
            disabled={isDisabled}
            readOnly={control.readonly}
            required={control.required}
            onChange={(e) => onChange?.(e.target.value)}
            onBlur={onBlur}
            onFocus={onFocus}
            aria-invalid={showError ? 'true' : 'false'}
            aria-required={control.required ? 'true' : 'false'}
            aria-describedby={describedBy}
          />
        );

      case 'date-range-picker': {
        const drVal = (value as { start?: string; end?: string } | undefined) ?? {};
        return (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="date"
              value={drVal.start || ''}
              disabled={isDisabled}
              max={drVal.end}
              onChange={(e) => onChange?.({ ...drVal, start: e.target.value || undefined })}
              onBlur={onBlur}
              style={{ ...inputStyle, flex: 1, minWidth: '140px', borderColor: showError ? '#ef4444' : undefined }}
              aria-label={`${control.label ?? ''} start date`}
              aria-invalid={showError ? 'true' : 'false'}
              aria-required={control.required ? 'true' : 'false'}
            />
            <span style={{ fontSize: '13px', color: theme === 'dark' ? '#94a3b8' : '#6b7280', flexShrink: 0 }}>to</span>
            <input
              type="date"
              value={drVal.end || ''}
              disabled={isDisabled}
              min={drVal.start}
              onChange={(e) => onChange?.({ ...drVal, end: e.target.value || undefined })}
              onBlur={onBlur}
              style={{ ...inputStyle, flex: 1, minWidth: '140px', borderColor: showError ? '#ef4444' : undefined }}
              aria-label={`${control.label ?? ''} end date`}
              aria-invalid={showError ? 'true' : 'false'}
            />
          </div>
        );
      }

      case 'rich-text-editor': {
        /**
         * This case reads `value` directly rather than `strValue`, and coerces it with its own
         * expression — a difference from `case 'textarea':` that is deliberately preserved rather
         * than tidied up, because changing it would change the flag-off DOM.
         *
         * Bound once, here, rather than written out at both the places that need it (the native
         * element's `value` and the trigger's seeded `value`). The two must agree — the trigger
         * opens on exactly what the field is showing — and two copies of the same expression is a
         * correctness requirement enforced by nothing.
         */
        const richTextValue = typeof value === 'string' ? value : (value ? String(value) : '');

        // Unchanged from before Plan/05 §3C — every attribute, in the same order, and the same
        // coerced `value`. Extracted to a const only so the opt-in branch below can render the
        // *same element* rather than a copy.
        const nativeRichTextEditor = (
          <textarea
            id={control.id}
            style={{ ...inputStyle, height: '200px', resize: 'vertical', borderColor: showError ? '#ef4444' : undefined }}
            value={richTextValue}
            placeholder={control.placeholder ?? 'Enter rich text content...'}
            disabled={isDisabled}
            readOnly={control.readonly}
            required={control.required}
            onChange={handleChange}
            onBlur={onBlur}
            onFocus={onFocus}
            aria-invalid={showError ? 'true' : 'false'}
            aria-required={control.required ? 'true' : 'false'}
            aria-describedby={describedBy}
          />
        );

        /**
         * Plan/05 §3C, same contract as `case 'text':`/`case 'textarea':` above — off unless a field
         * explicitly sets `config.expressionBuilder.enabled: true`, and the early return keeps the
         * flag-off DOM byte-identical to what this case rendered before §3C.
         * `FormField.expressionBuilder.test.tsx` asserts that against a fixture captured from the
         * pre-§3C source.
         *
         * No `control.type` check is needed here: unlike `case 'text':`, this case is not shared
         * with any other control type.
         */
        if (!isExpressionBuilderEnabled(control)) {
          return nativeRichTextEditor;
        }

        return (
          // `flex-start` like `textarea`: this element is 200px tall, so the trigger belongs at its
          // top edge rather than floating in the middle of the composing area.
          //
          // Plan/05 §3C anticipated having to find room beside a toolbar + contentEditable surface.
          // That is not what this case renders — see the note in the test file — so the trigger sits
          // exactly where `textarea`'s does, and there is no toolbar layout for it to disturb.
          <div className="af-expr-field-row" style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', width: '100%' }}>
            {nativeRichTextEditor}
            {/* Error boundary before `Suspense`, for the reason spelled out in `case 'text':`. */}
            <ExpressionBuilderErrorBoundary controlID={control.id} theme={theme}>
              <Suspense fallback={EXPRESSION_TRIGGER_SUSPENSE_FALLBACK}>
                <ExpressionBuilderTrigger
                  control={control}
                  value={richTextValue}
                  // Same path a keystroke takes. `sanitizeFieldValue` is **not** a pass-through for
                  // this control type (it falls to the `default:` arm, i.e. `sanitizeInput`), so
                  // confirmed markup is entity-encoded exactly as typed markup already is today.
                  // Bypassing it to keep raw HTML would make the builder an XSS hole into a field that
                  // filters everything else it accepts — see the test file for the full note.
                  //
                  // No `applyMaxLength` here, deliberately, and that is parity rather than an
                  // omission: this case's `<textarea>` carries **no** `maxLength` attribute (unlike
                  // `case 'text':`/`case 'textarea':`), so typing into it is uncapped. Capping only
                  // the builder would make it the stricter of the two doors into the same field.
                  onConfirm={(next) => onChange?.(sanitizeFieldValue(next))}
                  disabled={isDisabled || control.readonly === true}
                  theme={theme}
                  // Plan/05 §3C. `'html'` was this case's original default and is no longer: routed
                  // through the entity-encoding `sanitizeFieldValue` above, `'html'` mode added one
                  // nested `<p>` wrapper per save→reopen→confirm cycle. `RICH_TEXT_EXPRESSION_
                  // DEFAULT_FORMAT` now resolves to `'text'` — see that constant for the full
                  // reasoning, and note this case renders a plain `<textarea>`, not a markup editor.
                  // `'html'` remains selectable per field; a schema setting `outputFormat`
                  // explicitly still wins.
                  defaultOutputFormat={RICH_TEXT_EXPRESSION_DEFAULT_FORMAT}
                />
              </Suspense>
            </ExpressionBuilderErrorBoundary>
          </div>
        );
      }

      case 'code-editor':
        return (
          <textarea
            id={control.id}
            style={{ ...inputStyle, height: '240px', fontFamily: 'Consolas, Monaco, "Courier New", monospace',
              fontSize: '13px', lineHeight: '1.6', resize: 'vertical', borderColor: showError ? '#ef4444' : undefined }}
            value={typeof value === 'string' ? value : (value ? String(value) : '')}
            placeholder={control.placeholder ?? `// Enter ${(control.config?.language as string) || 'code'} here...`}
            disabled={isDisabled}
            readOnly={control.readonly}
            required={control.required}
            onChange={handleChange}
            onBlur={onBlur}
            onFocus={onFocus}
            spellCheck={false}
            aria-invalid={showError ? 'true' : 'false'}
            aria-required={control.required ? 'true' : 'false'}
            aria-describedby={describedBy}
          />
        );

      case 'tree-select': {
        // Hierarchical tree with expand/collapse and indented children
        type TreeNode = { value: string; label: string; children?: TreeNode[] };
        const treeData: TreeNode[] = (control.config?.options as TreeNode[] | undefined)
          ?? (control.config?.dataSource as TreeNode[] | undefined) ?? [];
        const [treeExpanded, setTreeExpanded] = React.useState<Set<string>>(new Set());
        const [treeOpen, setTreeOpen] = React.useState(false);
        const treeRef = React.useRef<HTMLDivElement>(null);
        const selectedLabel = (() => {
          const find = (nodes: TreeNode[]): string | null => {
            for (const n of nodes) {
              if (n.value === value) return n.label;
              if (n.children) { const r = find(n.children); if (r) return r; }
            }
            return null;
          };
          return find(treeData);
        })();

        const toggleExpand = (val: string, e: React.MouseEvent) => {
          e.stopPropagation();
          setTreeExpanded((prev) => { const n = new Set(prev); if (n.has(val)) n.delete(val); else n.add(val); return n; });
        };

        const renderNodes = (nodes: TreeNode[], depth: number): React.ReactNode =>
          nodes.map((node) => {
            const hasChildren = node.children && node.children.length > 0;
            const isExpanded = treeExpanded.has(node.value);
            const isSelected = node.value === value;
            return (
              <React.Fragment key={node.value}>
                <div
                  onClick={() => { onChange?.(node.value); setTreeOpen(false); }}
                  style={{
                    padding: '7px 10px', paddingLeft: `${12 + depth * 18}px`,
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px',
                    background: isSelected ? (theme === 'dark' ? 'rgba(34,197,94,0.15)' : '#f0fdf4') : 'transparent',
                    color: isSelected ? (theme === 'dark' ? '#4ade80' : '#15803d') : (theme === 'dark' ? '#e2e8f0' : '#374151'),
                    fontSize: '13px', fontWeight: isSelected ? 600 : 400,
                  }}
                  onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = theme === 'dark' ? 'rgba(255,255,255,0.05)' : '#f5f5f5'; }}
                  onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                >
                  {hasChildren ? (
                    <span onClick={(e) => toggleExpand(node.value, e)} style={{ width: 16, textAlign: 'center', flexShrink: 0, userSelect: 'none' }}>
                      {isExpanded ? '▾' : '▸'}
                    </span>
                  ) : <span style={{ width: 16, flexShrink: 0 }} />}
                  <span style={{ flex: 1 }}>{node.label}</span>
                  {isSelected && <span style={{ fontSize: 11 }}>✓</span>}
                </div>
                {hasChildren && isExpanded && renderNodes(node.children!, depth + 1)}
              </React.Fragment>
            );
          });

        return (
          <div ref={treeRef} style={{ position: 'relative' }}>
            <button type="button" onClick={() => setTreeOpen(!treeOpen)} disabled={isDisabled}
              style={{ ...inputStyle, borderColor: showError ? '#ef4444' : treeOpen ? (theme === 'dark' ? '#22c55e' : '#16a34a') : undefined,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: isDisabled ? 'not-allowed' : 'pointer', textAlign: 'left' }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {selectedLabel || <span style={{ opacity: 0.5 }}>Select...</span>}
              </span>
              <span style={{ fontSize: 10, color: '#999', flexShrink: 0 }}>{treeOpen ? <ChevronUpIcon width={10} height={10} /> : <ChevronDownIcon width={10} height={10} />}</span>
            </button>
            {treeOpen && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, zIndex: 100,
                background: theme === 'dark' ? '#1e293b' : '#fff',
                border: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.15)' : '#d1d5db'}`,
                borderRadius: 6, boxShadow: '0 4px 16px rgba(0,0,0,0.15)', maxHeight: 260, overflowY: 'auto',
              }}>
                {renderNodes(treeData, 0)}
              </div>
            )}
          </div>
        );
      }

      case 'cascading-select': {
        // Linked dropdowns — each level filters the next
        // Supports two data formats:
        // 1. Nested tree: config.options = [{ value, label, children: [...] }]
        // 2. Flat levels: config.levels = [{ id, label }] + config.dataSource = { "level-id": [{ id, label }] }
        type CascadeNode = { value?: string; id?: string; label: string; children?: CascadeNode[] };
        const cascadeTreeData: CascadeNode[] = (control.config?.options as CascadeNode[] | undefined) ?? [];
        const cascadeLevels = (control.config?.levels as Array<{ id: string; label: string; parentId?: string }> | undefined) ?? [];
        const cascadeFlatData = (control.config?.dataSource as Record<string, Array<{ id: string; label: string }>> | undefined) ?? {};
        const cascadeValue = Array.isArray(value) ? (value as string[]) : (typeof value === 'string' && value ? [value] : []);

        // Build display levels from whichever data format is available
        const levels: Array<{ label: string; options: Array<{ value: string; label: string }>; selected: string }> = [];

        if (cascadeTreeData.length > 0) {
          // Nested tree format
          let currentNodes: CascadeNode[] = cascadeTreeData;
          for (let i = 0; ; i++) {
            if (currentNodes.length === 0) break;
            const selected = cascadeValue[i] || '';
            levels.push({
              label: (control.config?.levelLabels as string[] | undefined)?.[i] || `Level ${i + 1}`,
              options: currentNodes.map(n => ({ value: n.value ?? n.id ?? '', label: n.label })),
              selected,
            });
            const found = currentNodes.find(n => (n.value ?? n.id) === selected);
            if (found?.children && found.children.length > 0) {
              currentNodes = found.children;
            } else {
              break;
            }
          }
        } else if (cascadeLevels.length > 0) {
          // Flat levels format from CascadingSelectEditor
          for (let i = 0; i < cascadeLevels.length; i++) {
            const lvl = cascadeLevels[i];
            const selected = cascadeValue[i] || '';
            let opts: Array<{ id: string; label: string }> = [];

            if (lvl.parentId) {
              // Child level — filter options based on parent's selected value
              const parentIndex = cascadeLevels.findIndex(l => l.id === lvl.parentId);
              const parentSelected = parentIndex >= 0 ? (cascadeValue[parentIndex] || '') : '';
              if (parentSelected) {
                const key = `${lvl.parentId}:${parentSelected}`;
                opts = cascadeFlatData[key] || cascadeFlatData[lvl.id] || [];
              }
              // If no parent selected, opts stays empty — no options shown
            } else {
              // Root level — show all options
              opts = cascadeFlatData[lvl.id] || [];
            }

            levels.push({
              label: lvl.label,
              options: opts.map(o => ({ value: o.id, label: o.label })),
              selected,
            });
          }
        }

        // Show at least one empty dropdown when no data configured
        if (levels.length === 0) {
          levels.push({ label: 'Level 1', options: [], selected: '' });
        }

        const handleCascadeChange = (levelIndex: number, val: string) => {
          const newValue = cascadeValue.slice(0, levelIndex);
          newValue[levelIndex] = val;
          onChange?.(newValue);
        };

        return (
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {levels.map((level, i) => (
              <div key={i} style={{ flex: 1, minWidth: '120px' }}>
                <div style={{ fontSize: '11px', fontWeight: 600, color: theme === 'dark' ? '#64748b' : '#9ca3af', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {level.label}
                </div>
                <select
                  style={{ ...inputStyle, borderColor: showError ? '#ef4444' : undefined }}
                  value={level.selected}
                  disabled={isDisabled}
                  onChange={(e) => handleCascadeChange(i, e.target.value)}
                  onBlur={onBlur}
                  aria-label={level.label}
                  aria-invalid={showError ? 'true' : 'false'}
                >
                  <option value="" disabled>Select...</option>
                  {level.options.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        );
      }

      case 'signature-pad': {
        const sigDataUrl = typeof value === 'string' ? value : '';
        return (
          <div style={{ border: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.15)' : '#d1d5db'}`, borderRadius: '6px', overflow: 'hidden' }}>
            {sigDataUrl ? (
              <div>
                <img src={sigDataUrl} alt="Signature" style={{ maxWidth: '100%', height: '120px', objectFit: 'contain', display: 'block' }} />
                {!isDisabled && (
                  <button type="button" onClick={() => onChange?.(undefined)}
                    style={{ padding: '4px 12px', fontSize: '12px', cursor: 'pointer', background: 'none', border: 'none', color: '#ef4444', display: 'block', width: '100%' }}>
                    Clear Signature
                  </button>
                )}
              </div>
            ) : (
              <div style={{ height: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: theme === 'dark' ? '#4b5563' : '#9ca3af', fontSize: '13px' }}>
                {isDisabled ? 'Signature field (disabled)' : 'Signature pad — use advanced controls for full functionality'}
              </div>
            )}
          </div>
        );
      }

      case 'button': {
        // Generic role-gating for ANY button control (config.requiredRoles?: string[]) --
        // the same real capability already used for grid row Edit/Delete actions
        // (GridControl.tsx's LinkColumnConfig.requiredRoles + utils/roleAccess.ts's
        // isInRole()), extended here so a standalone button (e.g. "Add New Document",
        // outside any grid) can be gated the exact same schema-driven way, not a
        // special-cased mechanism per button.
        const requiredRoles = control.config?.requiredRoles as string[] | undefined;
        const roleAllowed = isInRole(requiredRoles);
        const buttonDisabled = isDisabled || !roleAllowed;
        const buttonLabel = (control.config?.label as string) || control.label || 'Click';
        const buttonDisabledTitle = !roleAllowed ? 'You do not have permission to perform this action' : undefined;

        // Optional capability: config.createForm opens a linked Atlas Form empty, in a modal,
        // and POSTs the entered values as a real create/insert request on save (see
        // CreateRecordButton.tsx's own doc comment for why this needed a new small component
        // rather than reusing GridEditModal/LinkFormButton, which are both bound to existing
        // data instead of creating a brand-new record). On success, re-fires this form's own
        // 'search'-triggered apiAction (the same onButtonClick mechanism the Search button and
        // pagination already use) so a newly-created record shows up immediately.
        const createForm = control.config?.createForm as CreateFormConfig | undefined;
        if (createForm) {
          return (
            <Suspense fallback={<button type="button" style={getButtonStyle('primary', theme)} disabled>{buttonLabel}</button>}>
              <CreateRecordButton
                label={buttonLabel}
                buttonStyle={getButtonStyle('primary', theme)}
                disabled={buttonDisabled}
                disabledTitle={buttonDisabledTitle}
                config={createForm}
                onCreated={() => onButtonClick?.(control.id, formValues || {})}
              />
            </Suspense>
          );
        }

        return (
          <button type="button" style={getButtonStyle('primary', theme)}
            disabled={buttonDisabled}
            title={buttonDisabledTitle}
            onClick={() => {
              onButtonClick?.(control.id, formValues || {});
              onChange?.(true);
            }}>
            {buttonLabel}
          </button>
        );
      }

      // ── Server-side pagination ────────────────────────────────────────────
      // Real Prev/Next + "Page N" UI for forms whose apiActions send `pageInfo.pageNumber`
      // to a server-paginated endpoint (unlike GridControl's own `config.pageSize` pager,
      // which only re-slices rows already in the client). Clicking Prev/Next updates this
      // control's own numeric value (still readable via the existing payloadMapping to
      // `pageInfo.pageNumber`) and re-fires the form's 'search'-triggered apiAction via the
      // same `onButtonClick` mechanism a `type: 'button'` Search control already uses.
      case 'server-pagination': {
        const currentPage = Math.max(1, Number(value) || 1);
        const isFirstPage = currentPage <= 1;
        // Optional heuristic: when config.resultsControlID names the grid control holding
        // the last search's rows, an empty result set on a page > 1 means we've paged past
        // the end — disable Next rather than let the user page into a permanently-empty view.
        const resultsControlID = control.config?.resultsControlID as string | undefined;
        const resultsValue = resultsControlID ? formValues?.[resultsControlID] : undefined;

        // Optional "Page X of Y" indicator: config.totalControlID names a control (typically
        // hidden) whose value holds the search response's total record count, populated via
        // the same apiActions payloadMapping mechanism resultsControlID's grid already uses.
        // config.pageSizeControlID names the control holding the current page size (e.g. a
        // real page-size <select>); falls back to config.defaultPageSize, then 20.
        const totalControlID = control.config?.totalControlID as string | undefined;
        const totalCountRaw = totalControlID ? formValues?.[totalControlID] : undefined;
        const totalCount = typeof totalCountRaw === 'number' ? totalCountRaw : Number(totalCountRaw);
        const hasTotalCount = totalControlID !== undefined && Number.isFinite(totalCount);

        const pageSizeControlID = control.config?.pageSizeControlID as string | undefined;
        const pageSizeRaw = pageSizeControlID ? formValues?.[pageSizeControlID] : undefined;
        const pageSize = Number(pageSizeRaw) || Number(control.config?.defaultPageSize) || 20;
        const totalPages = hasTotalCount ? Math.max(1, Math.ceil(totalCount / pageSize)) : undefined;

        const pastLastPage = totalPages !== undefined
          ? currentPage >= totalPages
          : (Array.isArray(resultsValue) && resultsValue.length === 0 && currentPage > 1);

        const goToPage = (nextPage: number) => {
          const clamped = Math.max(1, nextPage);
          onChange?.(clamped);
          onButtonClick?.(control.id, { ...(formValues || {}), [control.id]: clamped });
        };

        const prevDisabled = isDisabled || isFirstPage;
        const nextDisabled = isDisabled || pastLastPage;
        // getButtonStyle() renders the same look regardless of the native `disabled`
        // attribute, so a disabled Prev/Next previously looked identical to an enabled one
        // (functionally inert but visually indistinguishable). Fade + block the pointer
        // explicitly here rather than changing getButtonStyle() itself, which is shared by
        // every button-rendering control on the form, not just pagination.
        const pagerButtonStyle = (isBtnDisabled: boolean): React.CSSProperties => ({
          ...getButtonStyle('secondary', theme),
          padding: '6px 12px',
          fontSize: '13px',
          opacity: isBtnDisabled ? 0.45 : 1,
          cursor: isBtnDisabled ? 'not-allowed' : 'pointer',
        });

        // Optional inline page-size picker, co-located in the SAME bordered/backgrounded bar
        // as Prev/Page-X-of-Y/Next (one compact "panel header" frame, not loose separate
        // controls) — config.pageSizeOptions lists the selectable sizes; requires
        // pageSizeControlID (above) to know which formValues key to read/write. Kept on the
        // pagination control's OWN config (rather than looked up from a separate control's
        // schema definition) because FormField only receives its own `control`, not the full
        // schema/control list.
        const pageSizeOptions = (control.config?.pageSizeOptions as number[] | undefined) ?? [10, 20, 50, 100];
        const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
          if (!pageSizeControlID) return;
          const nextSize = Number(e.target.value);
          onButtonClick?.(control.id, {
            ...(formValues || {}),
            [pageSizeControlID]: nextSize,
            [control.id]: 1, // reset to page 1 — the current page may not exist at the new size
          });
        };

        // Reuses this design system's existing "panel/toolbar" visual (same border, radius,
        // and subtle-overlay background tokens as `.af-grid__toolbar` / `.af-grid`'s own card
        // chrome in grid.css) rather than inventing a new bordered-box style — the pagination
        // bar now reads as one coherent panel, matching the grid it controls.
        const barStyle: React.CSSProperties = {
          display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
          padding: '8px 12px', borderRadius: '8px',
          border: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.08)' : '#e5e7eb'}`,
          background: theme === 'dark' ? 'rgba(255,255,255,0.03)' : '#f9fafb',
        };

        return (
          <div style={barStyle}>
            {pageSizeControlID && (
              <select
                value={String(pageSize)}
                disabled={isDisabled}
                onChange={handlePageSizeChange}
                aria-label="Page size"
                style={{ ...inputStyle, width: 'auto', maxWidth: '110px', padding: '6px 8px', fontSize: '13px' }}
              >
                {pageSizeOptions.map((size) => (
                  <option key={size} value={size}>{size} / page</option>
                ))}
              </select>
            )}
            <button type="button" style={pagerButtonStyle(prevDisabled)}
              disabled={prevDisabled}
              onClick={() => goToPage(currentPage - 1)}
              aria-label="Previous page">
              ‹ Prev
            </button>
            <span style={{ fontSize: '13px', fontWeight: 600, color: theme === 'dark' ? '#e5e7eb' : '#374151', whiteSpace: 'nowrap' }}>
              {totalPages !== undefined
                ? `Page ${currentPage} of ${totalPages}${hasTotalCount ? ` (${totalCount} total)` : ''}`
                : `Page ${currentPage}`}
            </span>
            <button type="button" style={pagerButtonStyle(nextDisabled)}
              disabled={nextDisabled}
              onClick={() => goToPage(currentPage + 1)}
              aria-label="Next page">
              Next ›
            </button>
          </div>
        );
      }

      case 'form-container':
        return (
          <Suspense fallback={<ControlLoadingFallback theme={theme} />}>
            <FormContainerControl
              control={control}
              value={value}
              onChange={onChange ?? (() => {})}
              onBlur={onBlur ?? (() => {})}
              disabled={isDisabled}
              mode={mode}
            />
          </Suspense>
        );

      // ── Tag Input ──────────────────────────────────────────────────────────
      case 'tag-input':
        return <TagInputControl control={control} theme={theme} mode={mode} inputStyle={inputStyle} isDisabled={isDisabled} value={value} onChange={onChange} onBlur={onBlur} />;

      // ── Multi-select search — searchable dropdown with tag chips ──────────
      case 'multi-select-search': {
        const mssRawOpts = (control.config?.options as Array<{ value?: string; id?: string; label: string }> | undefined) ?? [];
        const mssOpts = mssRawOpts.map(o => ({ value: o.value ?? o.id ?? '', label: o.label }));
        const mssSelected = Array.isArray(value) ? (value as string[]) : [];
        const [mssSearch, setMssSearch] = React.useState('');
        const [mssOpen, setMssOpen] = React.useState(false);
        const mssRef = React.useRef<HTMLDivElement>(null);
        const mssInputRef = React.useRef<HTMLInputElement>(null);

        const mssFiltered = mssOpts.filter(o =>
          !mssSelected.includes(o.value) &&
          (!mssSearch || o.label.toLowerCase().includes(mssSearch.toLowerCase()) || o.value.toLowerCase().includes(mssSearch.toLowerCase()))
        );

        // Close on click outside
        React.useEffect(() => {
          if (!mssOpen) return;
          const handler = (e: MouseEvent) => { if (mssRef.current && !mssRef.current.contains(e.target as Node)) setMssOpen(false); };
          document.addEventListener('mousedown', handler);
          return () => document.removeEventListener('mousedown', handler);
        }, [mssOpen]);

        return (
          <div ref={mssRef} style={{ position: 'relative' }}>
            {/* Selected tags + search input */}
            <div
              onClick={() => { if (!isDisabled) { setMssOpen(true); setTimeout(() => mssInputRef.current?.focus(), 50); } }}
              style={{
                ...inputStyle, borderColor: showError ? '#ef4444' : mssOpen ? (theme === 'dark' ? '#22c55e' : '#16a34a') : undefined,
                display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center',
                cursor: isDisabled ? 'not-allowed' : 'text', minHeight: '40px', padding: '6px 10px',
              }}
            >
              {mssSelected.map((v) => {
                const opt = mssOpts.find(o => o.value === v);
                return (
                  <span key={v} style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '12px',
                    background: theme === 'dark' ? 'rgba(34,197,94,0.15)' : '#dcfce7', color: theme === 'dark' ? '#4ade80' : '#15803d',
                    display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
                    {opt?.label ?? v}
                    {!isDisabled && <button type="button" onClick={(e) => { e.stopPropagation(); onChange?.(mssSelected.filter(x => x !== v)); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: '13px', color: 'inherit', lineHeight: 1 }}><CloseIcon width={11} height={11} /></button>}
                  </span>
                );
              })}
              <input
                ref={mssInputRef}
                type="text"
                value={mssSearch}
                onChange={(e) => { setMssSearch(e.target.value); if (!mssOpen) setMssOpen(true); }}
                onFocus={() => setMssOpen(true)}
                onKeyDown={(e) => {
                  if (e.key === 'Backspace' && !mssSearch && mssSelected.length > 0) {
                    onChange?.(mssSelected.slice(0, -1));
                  }
                }}
                placeholder={mssSelected.length === 0 ? 'Type to search...' : ''}
                disabled={isDisabled}
                style={{ border: 'none', outline: 'none', background: 'transparent', flex: 1, minWidth: '80px',
                  fontSize: '13px', color: theme === 'dark' ? '#e2e8f0' : '#374151', padding: '2px 0' }}
              />
            </div>

            {/* Dropdown */}
            {mssOpen && mssFiltered.length > 0 && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, zIndex: 100,
                background: theme === 'dark' ? '#1e293b' : '#fff',
                border: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.15)' : '#d1d5db'}`,
                borderRadius: 6, boxShadow: '0 4px 16px rgba(0,0,0,0.15)', maxHeight: 200, overflowY: 'auto',
              }}>
                {mssFiltered.map((opt) => (
                  <div key={opt.value}
                    onClick={() => { onChange?.([...mssSelected, opt.value]); setMssSearch(''); }}
                    style={{
                      padding: '8px 12px', fontSize: '13px', cursor: 'pointer',
                      color: theme === 'dark' ? '#e2e8f0' : '#374151',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = theme === 'dark' ? 'rgba(34,197,94,0.1)' : '#f0fdf4'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    {opt.label}
                  </div>
                ))}
              </div>
            )}
            {mssOpen && mssFiltered.length === 0 && mssSearch && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, zIndex: 100,
                background: theme === 'dark' ? '#1e293b' : '#fff',
                border: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.15)' : '#d1d5db'}`,
                borderRadius: 6, padding: '12px', textAlign: 'center', fontSize: '13px', color: '#999',
              }}>
                No matches for "{mssSearch}"
              </div>
            )}
          </div>
        );
      }

      // ── Key-Value Pairs ────────────────────────────────────────────────────
      case 'key-value-pairs': {
        const kvPairs = (Array.isArray(value) ? value : []) as Array<{ key: string; value: string }>;
        return (
          <div>
            {kvPairs.map((kv, i) => (
              <div key={`kv-${i}-${kv.key}`} style={{ display: 'flex', gap: '6px', marginBottom: '4px' }}>
                <input style={{ ...inputStyle, flex: 1 }} value={kv.key} placeholder="Key" disabled={isDisabled}
                  onChange={(e) => { const next = [...kvPairs]; next[i] = { ...kv, key: e.target.value }; onChange?.(next); }} />
                <input style={{ ...inputStyle, flex: 1 }} value={kv.value} placeholder="Value" disabled={isDisabled}
                  onChange={(e) => { const next = [...kvPairs]; next[i] = { ...kv, value: e.target.value }; onChange?.(next); }} />
                {!isDisabled && <button type="button" onClick={() => onChange?.(kvPairs.filter((_, j) => j !== i))}
                  style={{ background: 'none', border: `1px solid ${theme === 'dark' ? '#374151' : '#d1d5db'}`, borderRadius: '6px', cursor: 'pointer', padding: '0 8px', color: '#ef4444' }}><CloseIcon width={12} height={12} /></button>}
              </div>
            ))}
            {!isDisabled && <button type="button" onClick={() => onChange?.([...kvPairs, { key: '', value: '' }])}
              style={{ ...inputStyle, cursor: 'pointer', textAlign: 'center', color: theme === 'dark' ? '#22c55e' : '#16a34a', fontWeight: 600, fontSize: '13px' }}>
              + Add Pair
            </button>}
          </div>
        );
      }

      // ── Enhanced JSON Editor (monospace textarea) ──────────────────────────
      case 'enhanced-json-editor': {
        // Default placeholder mirrors the configured schema shape (array vs. object)
        // instead of always assuming an object — see control.config.schema.type.
        const jsonEditorSchemaType = (control.config?.schema as { type?: string } | undefined)?.type;
        const jsonEditorDefaultPlaceholder = jsonEditorSchemaType === 'array'
          ? '[\n  { "key": "value" }\n]'
          : '{\n  "key": "value"\n}';
        return (
          <textarea id={control.id}
            style={{ ...inputStyle, height: '200px', fontFamily: 'Consolas, Monaco, monospace', fontSize: '12px', lineHeight: '1.6', resize: 'vertical' }}
            value={typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value ?? '')}
            placeholder={control.placeholder ?? jsonEditorDefaultPlaceholder}
            disabled={isDisabled} spellCheck={false}
            onChange={(e) => { try { onChange?.(JSON.parse(e.target.value)); } catch { onChange?.(e.target.value); } }}
            onBlur={onBlur}
            aria-invalid={showError ? 'true' : 'false'}
            aria-required={control.required ? 'true' : 'false'}
            aria-describedby={describedBy} />
        );
      }

      // ── Code blocks (JS, CSS, HTML, Style) ────────────────────────────────
      case 'javascript-block':
      case 'style-block':
      case 'html-block': {
        const langMap: Record<string, string> = { 'javascript-block': 'JavaScript', 'style-block': 'CSS', 'html-block': 'HTML' };
        return (
          <textarea id={control.id}
            style={{ ...inputStyle, height: '200px', fontFamily: 'Consolas, Monaco, monospace', fontSize: '12px', lineHeight: '1.6', resize: 'vertical' }}
            value={typeof value === 'string' ? value : ''}
            placeholder={`// ${langMap[control.type] ?? 'Code'}...`}
            disabled={isDisabled} spellCheck={false}
            onChange={handleChange} onBlur={onBlur} />
        );
      }

      // ── File upload ────────────────────────────────────────────────────────
      case 'file-upload':
      case 'document-scanner':
      case 'css-file-selector':
      case 'js-file-selector':
      case 'asset-manager': {
        const fileLabel: Record<string, string> = {
          'file-upload': 'Choose file', 'document-scanner': 'Scan document',
          'css-file-selector': 'Select CSS file', 'js-file-selector': 'Select JS file', 'asset-manager': 'Manage assets',
        };
        const acceptMap: Record<string, string> = {
          'file-upload': (control.config?.accept as string) || '*/*',
          'document-scanner': 'image/*,.pdf',
          'css-file-selector': '.css,text/css',
          'js-file-selector': '.js,.mjs,application/javascript',
          'asset-manager': 'image/*,video/*,audio/*,.pdf,.svg',
        };
        const fileInputId = `${control.id}-file-input`;
        const isMultipleMode = control.config?.multiple === true;

        // Handle both single File and File[] values
        let fileNames: string[] = [];
        if (Array.isArray(value)) {
          fileNames = (value as File[]).map(f => f.name);
        } else if (value instanceof File) {
          fileNames = [(value as File).name];
        } else if (typeof value === 'string' && value) {
          fileNames = [value];
        }

        const containerClasses = [
          'af-file-upload__container',
          theme === 'dark' && 'af-file-upload__container--dark',
          isDisabled && 'af-file-upload__container--disabled',
        ].filter(Boolean).join(' ');
        return (
          <div
            onClick={() => { if (!isDisabled) document.getElementById(fileInputId)?.click(); }}
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onDrop={(e) => {
              try {
                e.preventDefault(); e.stopPropagation();

                // ← NULL SAFETY: Check dataTransfer exists
                if (isDisabled || !e.dataTransfer?.files) {
                  if (!e.dataTransfer) {
                    console.warn('[FormField] ⚠️ Drop event has no dataTransfer');
                  }
                  return;
                }

                const allDroppedFiles = Array.from(e.dataTransfer.files);

                // ← NULL SAFETY: Check files array is not empty
                if (!allDroppedFiles || allDroppedFiles.length === 0) {
                  console.log('[FormField] ℹ️ No files in drop event');
                  return;
                }

                const isMultipleMode = control.config?.multiple === true;

                console.log('[FormField] file-upload onDrop event:', {
                  controlId: control.id,
                  droppedFilesCount: allDroppedFiles.length,
                  isMultipleMode: isMultipleMode,
                  files: allDroppedFiles.map(f => ({ name: f.name, size: f.size, type: f.type })),
                });

                // ← VALIDATION: Check file limits before processing
                const validation = validateFiles(allDroppedFiles);
                if (!validation.valid) {
                  console.error('[FormField] ❌ File validation failed (drag-drop):', validation.error);
                  alert(`File validation failed: ${validation.error}`);
                  return;
                }

                // ← REFACTORING: Use extracted helper to get files and log
                const filesToPass = getFilesForChangeEvent(allDroppedFiles, isMultipleMode);
                logFileSelection(allDroppedFiles, isMultipleMode, control.id, true);
                onChange?.(filesToPass);
              } catch (error) {
                console.error('[FormField] ❌ Unexpected error in onDrop handler:', error);
              }
            }}
            className={containerClasses}
          >
            <input
              id={fileInputId}
              type="file"
              accept={acceptMap[control.type] || '*/*'}
              multiple={!!control.config?.multiple}
              disabled={isDisabled}
              onChange={(e) => {
                try {
                  // ← NULL SAFETY: Check target and files exist
                  if (!e?.target?.files) {
                    console.warn('[FormField] ⚠️ onChange event has no files');
                    return;
                  }

                  const allSelectedFiles = Array.from(e.target.files);
                  const isMultipleMode = control.config?.multiple === true;

                  console.log('[FormField] file-upload onChange event:', {
                    controlId: control.id,
                    selectedFilesCount: allSelectedFiles.length,
                    isMultipleMode: isMultipleMode,
                    files: allSelectedFiles.map(f => ({ name: f.name, size: f.size, type: f.type })),
                  });

                  if (allSelectedFiles.length === 0) {
                    console.log('[FormField] ℹ️ No files selected');
                    return;
                  }

                  // ← VALIDATION: Check file limits before processing
                  const validation = validateFiles(allSelectedFiles);
                  if (!validation.valid) {
                    console.error('[FormField] ❌ File validation failed:', validation.error);
                    alert(`File validation failed: ${validation.error}`);
                    if (e?.target) {
                      e.target.value = '';
                    }
                    return;
                  }

                  // ← REFACTORING: Use extracted helper to get files and log
                  const filesToPass = getFilesForChangeEvent(allSelectedFiles, isMultipleMode);
                  logFileSelection(allSelectedFiles, isMultipleMode, control.id, false);
                  onChange?.(filesToPass);

                  // ← NULL SAFETY: Check target exists before clearing
                  if (e?.target) {
                    e.target.value = '';
                  }
                } catch (error) {
                  console.error('[FormField] ❌ Unexpected error in onChange handler:', error);
                }
              }}
              className="af-file-upload__input"
            />
            <div className="af-file-upload__icon">📁</div>
            <div className="af-file-upload__label">
              {fileLabel[control.type] ?? 'Upload file'}
            </div>
            <div className="af-file-upload__hint">
              Drag & drop or click to browse
            </div>
            {fileNames.length > 0 && (
              <div className="af-file-upload__file-name">
                {isMultipleMode ? (
                  <>
                    <div style={{ fontWeight: '600', marginBottom: '8px' }}>
                      ✅ {fileNames.length} file{fileNames.length !== 1 ? 's' : ''} selected:
                    </div>
                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                      {fileNames.map((name, idx) => (
                        <li key={idx} style={{ marginBottom: '4px' }}>
                          {name}
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <div>File: {fileNames[0]}</div>
                )}
              </div>
            )}
          </div>
        );
      }

      // ── Grid (interactive, config-driven) ─────────────────────────────────
      case 'grid': {
        return (
          <Suspense fallback={<div className="af-grid__loading">Loading grid…</div>}>
            <GridControl
              control={control}
              value={value}
              onChange={onChange ?? (() => {})}
              onBlur={() => {}}
              mode={mode}
            />
          </Suspense>
        );
      }

      // ── Data table / Table (read-only display) ─────────────────────────────
      case 'data-table':
      case 'table': {
        const defaultTableData = [
          { id: 1, name: 'Alice Johnson', email: 'alice@example.com', role: 'Admin', status: 'Active' },
          { id: 2, name: 'Bob Smith', email: 'bob@example.com', role: 'Editor', status: 'Active' },
          { id: 3, name: 'Carol White', email: 'carol@example.com', role: 'Viewer', status: 'Inactive' },
          { id: 4, name: 'Dan Brown', email: 'dan@example.com', role: 'Editor', status: 'Active' },
          { id: 5, name: 'Eve Davis', email: 'eve@example.com', role: 'Admin', status: 'Active' },
        ];
        const rows = (control.config?.data as Array<Record<string, unknown>> | undefined)
          ?? (Array.isArray(value) ? value as Array<Record<string, unknown>> : null)
          ?? defaultTableData;
        const cols = (control.config?.columns as Array<{ key: string; label?: string }> | undefined)
          ?? (rows.length > 0 ? Object.keys(rows[0]).map(k => ({ key: k, label: k.charAt(0).toUpperCase() + k.slice(1) })) : []);
        const thStyle: React.CSSProperties = {
          padding: '10px 14px', textAlign: 'left', fontWeight: 700, fontSize: '11px',
          textTransform: 'uppercase', letterSpacing: '0.05em',
          borderBottom: `2px solid ${theme === 'dark' ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}`,
          color: theme === 'dark' ? '#64748b' : '#6b7280',
          background: theme === 'dark' ? 'rgba(255,255,255,0.03)' : '#f9fafb',
        };
        const tdStyle: React.CSSProperties = {
          padding: '9px 14px', fontSize: '13px',
          color: theme === 'dark' ? '#e2e8f0' : '#374151',
          borderBottom: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.05)' : '#f1f5f9'}`,
        };
        const statusStyle = (val: unknown): React.CSSProperties | undefined => {
          const s = String(val).toLowerCase();
          if (s === 'active') return { padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600, background: theme === 'dark' ? 'rgba(34,197,94,0.15)' : '#dcfce7', color: theme === 'dark' ? '#4ade80' : '#15803d' };
          if (s === 'inactive') return { padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600, background: theme === 'dark' ? 'rgba(239,68,68,0.15)' : '#fee2e2', color: theme === 'dark' ? '#f87171' : '#b91c1c' };
          return undefined;
        };
        return (
          <div style={{
            border: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.08)' : '#e5e7eb'}`,
            borderRadius: '8px', overflow: 'hidden',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '10px 14px',
              background: theme === 'dark' ? 'rgba(255,255,255,0.02)' : '#fff',
              borderBottom: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f5f9'}`,
            }}>
              <span style={{ fontSize: '13px', fontWeight: 600, color: theme === 'dark' ? '#e2e8f0' : '#111827' }}>
                {control.label || 'Data Table'}
              </span>
              <span style={{ fontSize: '11px', color: theme === 'dark' ? '#64748b' : '#9ca3af' }}>
                {rows.length} row{rows.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div style={{ overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {cols.map((c) => (
                      <th key={c.key} style={thStyle}>{c.label ?? c.key}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, ri) => (
                    <tr key={ri} style={{ background: ri % 2 === 1 ? (theme === 'dark' ? 'rgba(255,255,255,0.015)' : '#fafafa') : 'transparent' }}>
                      {cols.map((c) => {
                        const cellVal = (row as any)[c.key];
                        const badge = statusStyle(cellVal);
                        return (
                          <td key={c.key} style={tdStyle}>
                            {badge ? <span style={badge}>{String(cellVal)}</span> : String(cellVal ?? '')}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '8px 14px', fontSize: '11px',
              borderTop: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f5f9'}`,
              color: theme === 'dark' ? '#475569' : '#9ca3af',
              background: theme === 'dark' ? 'rgba(255,255,255,0.02)' : '#fff',
            }}>
              <span>Showing 1-{rows.length} of {rows.length}</span>
              <div style={{ display: 'flex', gap: '4px' }}>
                <button type="button" disabled style={{ padding: '3px 8px', borderRadius: '4px', border: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}`, background: 'transparent', color: 'inherit', fontSize: '11px', cursor: 'not-allowed', opacity: 0.4 }}>Prev</button>
                <button type="button" style={{ padding: '3px 8px', borderRadius: '4px', border: 'none', background: theme === 'dark' ? '#22c55e' : '#16a34a', color: '#fff', fontSize: '11px', fontWeight: 700 }}>1</button>
                <button type="button" disabled style={{ padding: '3px 8px', borderRadius: '4px', border: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}`, background: 'transparent', color: 'inherit', fontSize: '11px', cursor: 'not-allowed', opacity: 0.4 }}>Next</button>
              </div>
            </div>
          </div>
        );
      }

      // ── Card Container ─────────────────────────────────────────────────────
      case 'card-container': {
        const cardChildren = (control.config?.controls as FormControl[] | undefined) ?? [];
        return (
          <div style={{
            border: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}`,
            borderRadius: '10px', overflow: 'hidden',
            background: theme === 'dark' ? 'rgba(255,255,255,0.03)' : '#fff',
            boxShadow: theme === 'dark' ? '0 2px 8px rgba(0,0,0,0.3)' : '0 1px 4px rgba(0,0,0,0.08)',
          }}>
            {control.label && (
              <div style={{ padding: '12px 16px', fontWeight: 600, fontSize: '14px',
                borderBottom: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f5f9'}`,
                color: theme === 'dark' ? '#e2e8f0' : '#111827' }}>
                {control.label}
              </div>
            )}
            <div style={{ padding: '16px' }}>
              {cardChildren.length > 0
                ? cardChildren.map((child) => <FormField key={child.id} control={child} value={undefined} mode={mode} onChange={() => {}} />)
                : <div style={{ fontSize: '13px', color: theme === 'dark' ? '#475569' : '#9ca3af', textAlign: 'center', padding: '12px' }}>Card content area</div>}
            </div>
          </div>
        );
      }

      // ── Collapsible Panel ──────────────────────────────────────────────────
      case 'collapsible-panel':
        return <CollapsiblePanelControl control={control} theme={theme} mode={mode} inputStyle={inputStyle} isDisabled={isDisabled} value={value} onChange={onChange} onBlur={onBlur} />;

      // ── Accordion ──────────────────────────────────────────────────────────
      case 'accordion':
        return <AccordionControl control={control} theme={theme} mode={mode} inputStyle={inputStyle} isDisabled={isDisabled} value={value} onChange={onChange} onBlur={onBlur} />;

      // ── Enhanced Tabs ──────────────────────────────────────────────────────
      case 'enhanced-tabs':
      case 'tab':
        return <EnhancedTabsControl control={control} theme={theme} mode={mode} inputStyle={inputStyle} isDisabled={isDisabled} value={value} onChange={onChange} onBlur={onBlur} />;

      // ── Modal ──────────────────────────────────────────────────────────────
      case 'modal':
        return <ModalControl control={control} theme={theme} mode={mode} inputStyle={inputStyle} isDisabled={isDisabled} value={value} onChange={onChange} onBlur={onBlur} />;

      // ── Stepper ────────────────────────────────────────────────────────────
      case 'stepper':
        return <StepperControl control={control} theme={theme} mode={mode} inputStyle={inputStyle} isDisabled={isDisabled} value={value} onChange={onChange} onBlur={onBlur} />;

      // ── Navigation controls ────────────────────────────────────────────────
      case 'breadcrumb': {
        const crumbs = (control.config?.items as Array<{ label: string }> | undefined) ?? [{ label: 'Home' }, { label: 'Section' }, { label: 'Page' }];
        return (
          <nav style={{ fontSize: '13px', color: theme === 'dark' ? '#94a3b8' : '#6b7280' }}>
            {crumbs.map((c, i) => (
              <span key={i}>
                {i > 0 && <span style={{ margin: '0 6px', opacity: 0.5 }}>/</span>}
                <span style={i === crumbs.length - 1 ? { fontWeight: 600, color: theme === 'dark' ? '#e2e8f0' : '#111827' } : { cursor: 'pointer' }}>{c.label}</span>
              </span>
            ))}
          </nav>
        );
      }
      case 'sidebar-nav': {
        const navItems = (control.config?.items as Array<{ label: string }> | undefined) ?? [{ label: 'Item 1' }, { label: 'Item 2' }, { label: 'Item 3' }];
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', maxWidth: '200px' }}>
            {navItems.map((item, i) => (
              <div key={i} style={{
                padding: '8px 12px', borderRadius: '6px', fontSize: '13px', cursor: 'pointer',
                background: i === 0 ? (theme === 'dark' ? 'rgba(34,197,94,0.12)' : '#dcfce7') : 'transparent',
                color: i === 0 ? (theme === 'dark' ? '#22c55e' : '#16a34a') : (theme === 'dark' ? '#94a3b8' : '#6b7280'),
                fontWeight: i === 0 ? 600 : 400,
              }}>{item.label}</div>
            ))}
          </div>
        );
      }
      case 'link': {
        // config?.link is set when schema went through parseSchemaObject(); (control as any).link
        // is the fallback for schemas loaded directly from the API without the parser (most paths).
        const linkCfg = (control.config?.link ?? (control as any).link) as {
          label?: string; showAs?: string; variant?: string; icon?: string;
          action?: { kind: string; formId?: number; target?: string; binding?: string };
        } | undefined;

        if (linkCfg?.action?.kind === 'form' && linkCfg.action.formId) {
          return (
            <Suspense fallback={<span style={{ fontSize: '13px', opacity: 0.5 }}>Loading…</span>}>
              <LinkFormButton
                linkCfg={linkCfg}
                value={value}
                onChange={onChange}
              />
            </Suspense>
          );
        }

        const href = (typeof value === 'string' && value) || (control.config?.href as string) || '#';
        const safeHref = /^(https?:\/\/|mailto:|tel:)/i.test(href) ? href : '#';
        return (
          <a href={safeHref} target="_blank" rel="noopener noreferrer"
            onClick={(e) => { if (safeHref === '#') e.preventDefault(); }}
            style={{ color: theme === 'dark' ? '#22c55e' : '#16a34a', fontSize: '14px', textDecoration: 'underline', cursor: safeHref === '#' ? 'default' : 'pointer' }}>
            {control.label || href}
          </a>
        );
      }

      // ── Special viewer controls ────────────────────────────────────────────
      case 'api-response-viewer':
      case 'variable-inspector':
      case 'conditional-logic-viewer':
      case 'database-query-builder':
        return (
          <pre style={{
            ...inputStyle, height: '120px', fontFamily: 'Consolas, Monaco, monospace',
            fontSize: '12px', lineHeight: '1.5', overflow: 'auto', whiteSpace: 'pre-wrap',
            background: theme === 'dark' ? 'rgba(0,0,0,0.3)' : '#f8fafc',
          }}>
            {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value ?? `// ${control.type.replace(/-/g, ' ')}`)}
          </pre>
        );

      // ── Timeline ───────────────────────────────────────────────────────────
      case 'timeline': {
        const items = (control.config?.items as Array<{ label: string; date?: string }> | undefined)
          ?? [{ label: 'Step 1', date: 'Start' }, { label: 'Step 2', date: 'In Progress' }, { label: 'Step 3', date: 'End' }];
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0', paddingLeft: '12px' }}>
            {items.map((item, i) => (
              <div key={i} style={{ display: 'flex', gap: '12px', position: 'relative', paddingBottom: i < items.length - 1 ? '20px' : 0 }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                  <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: theme === 'dark' ? '#22c55e' : '#16a34a', zIndex: 1 }} />
                  {i < items.length - 1 && <div style={{ width: '2px', flex: 1, background: theme === 'dark' ? 'rgba(34,197,94,0.3)' : '#d1d5db' }} />}
                </div>
                <div style={{ paddingBottom: '4px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: theme === 'dark' ? '#e2e8f0' : '#111827' }}>{item.label}</div>
                  {item.date && <div style={{ fontSize: '11px', color: theme === 'dark' ? '#64748b' : '#9ca3af' }}>{item.date}</div>}
                </div>
              </div>
            ))}
          </div>
        );
      }

      // ── Media controls ─────────────────────────────────────────────────────
      case 'image-gallery':
      case 'document-viewer':
      case 'enhanced-video-player':
      case 'audio-playlist':
      case 'map':
      case 'location-picker':
        return (
          <div style={{
            border: `2px dashed ${theme === 'dark' ? 'rgba(59,130,246,0.3)' : '#bfdbfe'}`,
            borderRadius: '8px', padding: '24px', textAlign: 'center',
            background: theme === 'dark' ? 'rgba(59,130,246,0.04)' : '#eff6ff',
            minHeight: '100px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px',
          }}>
            <div style={{ fontSize: '28px', opacity: 0.4 }}>
              {control.type === 'map' || control.type === 'location-picker' ? '🗺️' : control.type === 'image-gallery' ? '🖼️' : control.type === 'document-viewer' ? '📄' : '🎬'}
            </div>
            <div style={{ fontSize: '13px', fontWeight: 600, color: theme === 'dark' ? '#94a3b8' : '#6b7280', textTransform: 'capitalize' }}>
              {control.type.replace(/-/g, ' ')}
            </div>
            {value && <div style={{ fontSize: '12px', color: theme === 'dark' ? '#64748b' : '#9ca3af' }}>{String(value)}</div>}
          </div>
        );

      // ── QR Code Generator ──────────────────────────────────────────────────
      case 'qr-code-generator':
        return <QrCodeRenderer control={control} value={value} theme={theme} />;

      case 'barcode-generator':
        return <BarcodeRenderer control={control} value={value} theme={theme} />;

      // ── Captcha Widget (Placeholder) ──────────────────────────────────────
      case 'captcha-widget': {
        return (
          <div style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: '240px', height: '80px',
            border: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}`,
            borderRadius: '8px', background: theme === 'dark' ? 'rgba(255,255,255,0.03)' : '#f9fafb',
            fontSize: '14px', color: theme === 'dark' ? '#94a3b8' : '#6b7280',
          }}>
            🔒 Captcha
          </div>
        );
      }

      // ── Password strength meter ────────────────────────────────────────────
      case 'password-strength-meter': {
        const strength = Number(value ?? 0);
        const max5 = Math.min(5, Math.max(0, strength));
        const strengthColors = ['#ef4444', '#f97316', '#eab308', '#84cc16', '#22c55e'];
        const strengthLabels = ['Very Weak', 'Weak', 'Fair', 'Strong', 'Very Strong'];
        return (
          <div>
            <div style={{ display: 'flex', gap: '4px', marginBottom: '4px' }}>
              {[0, 1, 2, 3, 4].map(i => (
                <div key={i} style={{ flex: 1, height: '6px', borderRadius: '3px',
                  background: i < max5 ? strengthColors[max5 - 1] : (theme === 'dark' ? 'rgba(255,255,255,0.08)' : '#e5e7eb') }} />
              ))}
            </div>
            {max5 > 0 && <div style={{ fontSize: '11px', color: strengthColors[max5 - 1], fontWeight: 600 }}>{strengthLabels[max5 - 1]}</div>}
          </div>
        );
      }

      // ── Toggle — same visual as switch ────────────────────────────────────
      case 'toggle': {
        const isOn = Boolean(value);
        return (
          <button type="button" role="switch" aria-checked={isOn} aria-label={sanitizedLabel} disabled={isDisabled}
            onClick={() => onChange?.(!isOn)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              background: 'none', border: 'none', cursor: isDisabled ? 'not-allowed' : 'pointer',
              padding: 0, fontSize: '14px', color: theme === 'dark' ? '#e5e7eb' : '#374151',
            }}>
            <span style={{
              width: '44px', height: '24px', borderRadius: '12px', position: 'relative',
              background: isOn ? '#22c55e' : (theme === 'dark' ? '#374151' : '#d1d5db'),
              transition: 'background 0.2s', display: 'inline-block',
            }}>
              <span style={{
                position: 'absolute', top: '3px',
                left: isOn ? '23px' : '3px',
                width: '18px', height: '18px', borderRadius: '50%',
                background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
              }} />
            </span>
            <span>{isOn
              ? ((control.config?.onLabel as string) || 'On')
              : ((control.config?.offLabel as string) || 'Off')}
            </span>
          </button>
        );
      }

      // ── User Picker — text input for a single user name / email ──────────
      case 'user-picker': {
        return (
          <input id={control.id} type="text"
            style={{ ...inputStyle, borderColor: showError ? '#ef4444' : undefined }}
            value={strValue}
            placeholder={control.placeholder ?? 'Enter user name or email'}
            disabled={isDisabled}
            onChange={handleChange}
            onBlur={onBlur}
            onFocus={onFocus}
            aria-invalid={showError ? 'true' : 'false'}
            aria-required={control.required ? 'true' : 'false'}
            aria-describedby={describedBy} />
        );
      }

      // ── Actor List — comma-separated list of actors ───────────────────────
      case 'actor-list': {
        const actorItems: string[] = Array.isArray(value)
          ? (value as string[])
          : typeof value === 'string' && value.trim()
            ? value.split(',').map((s: string) => s.trim())
            : [];
        return (
          <input id={control.id} type="text"
            style={{ ...inputStyle, borderColor: showError ? '#ef4444' : undefined }}
            value={actorItems.join(', ')}
            placeholder={control.placeholder ?? 'Enter actors (comma-separated)'}
            disabled={isDisabled}
            onChange={(e) => {
              const raw = e.target.value;
              const items = raw.split(',').map((s: string) => s.trim()).filter(Boolean);
              onChange?.(items.length > 0 ? items : raw);
            }}
            onBlur={onBlur}
            onFocus={onFocus}
            aria-invalid={showError ? 'true' : 'false'}
            aria-required={control.required ? 'true' : 'false'}
            aria-describedby={describedBy} />
        );
      }

      default:
        try {
          const registry = getGlobalReactRegistry();
          const CustomComponent = registry?.getComponent(control.type, mode);
          if (CustomComponent) {
            return (
              <CustomComponent
                control={control}
                value={value}
                onChange={onChange ?? (() => {})}
                onBlur={onBlur ?? (() => {})}
                onFocus={onFocus}
                error={error}
                touched={touched}
                disabled={disabled}
                mode={mode}
              />
            );
          }
        } catch (err) {
          console.warn(`[FormField] Error loading custom control "${control.type}":`, err);
        }
        // Show loading placeholder for known-advanced types instead of confusing text input
        if (KNOWN_ADVANCED_TYPES.has(control.type)) {
          return (
            <div style={{ padding: '16px', border: '2px dashed #ddd', borderRadius: '6px',
              textAlign: 'center', color: '#999', fontSize: '13px' }}>
              Loading {control.type.replace(/-/g, ' ')}...
            </div>
          );
        }
        if ((globalThis as any).process?.env?.NODE_ENV !== 'production') {
          console.warn(`[FormField] Unrecognized control type "${control.type}" — rendering as text input`);
        }
        return (
          <input id={control.id} type="text"
            style={{ ...inputStyle, borderColor: showError ? '#ef4444' : undefined }}
            value={strValue}
            placeholder={control.placeholder ?? `(${control.type})`}
            disabled={isDisabled}
            onChange={handleChange}
            onBlur={onBlur}
            onFocus={onFocus} />
        );
    }
  };

  // Switch: pill left, label right, On/Off text inside the pill
  if (control.type === 'switch') {
    return (
      <div style={fieldWrapperStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {renderControl()}
          <label style={{ ...labelStyle, marginBottom: 0, cursor: 'pointer' }}
            onClick={() => !isDisabled && onChange?.(!Boolean(value))}>
            {sanitizedLabel}
            {control.required && modeConfig.showRequired && <span style={{ color: '#ef4444', marginLeft: '3px' }}>*</span>}
          </label>
        </div>
        {sanitizedHelp && modeConfig.showHelpText && <p style={helpStyle}>{sanitizedHelp}</p>}
        {showError && modeConfig.showValidation && (
          <p style={getErrorStyle()} id={errorId} role="alert">{sanitizedError}</p>
        )}
      </div>
    );
  }

  // Button controls: skip the generic label wrapper below entirely. A button's own text
  // (control.config.label ?? control.label — see the 'button' case in renderControl)
  // already serves as its label, per standard UI convention; rendering control.label a
  // second time as a <label> above it produced a visible duplicate ("Search" / "Search").
  // Platform-level fix — every Atlas Form with a `type: "button"` control benefits, not
  // just this one schema.
  //
  // Alignment is deliberately NOT special-cased here: `control.styles.containerStyle`
  // (ControlStyleSet, resolved by FormRenderer via `resolveStyles()` and applied to this
  // control's `.af-field-{id}` wrapper — the element one level *above* this component)
  // is already a real, generic, schema-authorable mechanism supporting
  // `display: 'flex'` + `justifyContent: 'flex-end'/'center'/...` for ANY control, not
  // just buttons. A schema that wants a right-aligned button sets that on the control's
  // own `styles.containerStyle` — no new field needed, and this inner wrapper stays a
  // plain block so it composes correctly as a flex item when the outer one is.
  if (control.type === 'button') {
    return (
      <div style={fieldWrapperStyle}>
        {renderControl()}
        {sanitizedHelp && modeConfig.showHelpText && <p style={helpStyle} id={helpId}>{sanitizedHelp}</p>}
        {showError && modeConfig.showValidation && (
          <p style={getErrorStyle()} id={errorId} role="alert">{sanitizedError}</p>
        )}
      </div>
    );
  }

  // Grid controls: skip the generic label wrapper for the same reason buttons do above. A
  // `type: "grid"` control renders its own header row (column titles) immediately below, and
  // is very commonly placed inside a titled `FormSection` (e.g. a "Documents" results panel) —
  // rendering `control.label` as a floating `<label>` directly above it produced a visible
  // duplicate heading ("Documents" section title, then "Documents" control label, then the
  // grid's own column headers). Platform-level fix — every Atlas Form with a `type: "grid"`
  // control benefits, not just this one schema. Description/help/error are still rendered
  // when present, same as the button path above.
  if (control.type === 'grid') {
    return (
      <div style={fieldWrapperStyle}>
        {control.description && (
          <p style={{ fontSize: '12px', color: theme === 'dark' ? '#94a3b8' : '#6b7280', margin: '0 0 6px', lineHeight: 1.4 }}>
            {control.description}
          </p>
        )}
        {renderControl()}
        {sanitizedHelp && modeConfig.showHelpText && <p style={helpStyle} id={helpId}>{sanitizedHelp}</p>}
        {showError && modeConfig.showValidation && (
          <p style={getErrorStyle()} id={errorId} role="alert">{sanitizedError}</p>
        )}
      </div>
    );
  }

  return (
    <div style={fieldWrapperStyle}>
      <label htmlFor={control.id} style={labelStyle}>
        {sanitizedLabel}
        {control.required && modeConfig.showRequired && <span style={{ color: '#ef4444', marginLeft: '3px' }}>*</span>}
      </label>
      {control.description && (
        <p style={{ fontSize: '12px', color: theme === 'dark' ? '#94a3b8' : '#6b7280', margin: '0 0 6px', lineHeight: 1.4 }}>
          {control.description}
        </p>
      )}
      {renderControl()}
      {sanitizedHelp && modeConfig.showHelpText && <p style={helpStyle} id={helpId}>{sanitizedHelp}</p>}
      {showError && modeConfig.showValidation && (
        <p style={getErrorStyle()} id={errorId} role="alert">{sanitizedError}</p>
      )}
    </div>
  );
};

FormField.displayName = 'FormField';
